import { Controller } from "@hotwired/stimulus"
import { checkInviteEmail } from "../lib/member_picker"
import { track } from "../lib/mixpanel"

// Add many action items at once (Keane K3-38 + K3-40, Phelipe 2026-10-13).
// Three ways in — files, the submittal register from spec sections, or a
// pasted / dictated list — one AI read, one review table where every row is
// checked and edited, then one save. Lives on the Action Items page root next
// to the list's own controller; the markup is punchlists/_import_modal.
// The submittal register's two orders (Phelipe 2026-10-15): By construction
// (default) — the stages a building goes up in, each stage's divisions in
// that order — or By division, MasterFormat number order. A row's division
// is the first two digits of its CSI spec section; anything else (a sheet
// with no section, a DOT-style "801") goes last.
const DIVISIONS = {
  "00": "Procurement and Contracting", "01": "General Requirements", "02": "Existing Conditions", "03": "Concrete", "04": "Masonry",
  "05": "Metals", "06": "Wood, Plastics, and Composites", "07": "Thermal and Moisture Protection", "08": "Openings", "09": "Finishes",
  "10": "Specialties", "11": "Equipment", "12": "Furnishings", "13": "Special Construction", "14": "Conveying Equipment",
  "21": "Fire Suppression", "22": "Plumbing", "23": "HVAC", "25": "Integrated Automation", "26": "Electrical", "27": "Communications",
  "28": "Electronic Safety and Security", "31": "Earthwork", "32": "Exterior Improvements", "33": "Utilities", "34": "Transportation",
  "35": "Waterway and Marine", "40": "Process Interconnections", "41": "Material Processing", "42": "Process Heating and Cooling",
  "43": "Process Gas and Liquid", "44": "Pollution and Waste Control", "46": "Water and Wastewater Equipment", "48": "Power Generation"
}
const STAGES = [
  ["Getting started", ["00", "01"]],
  ["Site work and demolition", ["02", "31", "33"]],
  ["Structure", ["03", "04", "05", "06"]],
  ["Building envelope", ["07", "08"]],
  ["Building systems", ["21", "22", "23", "25", "26", "27", "28"]],
  ["Interiors and finishes", ["09", "10", "12"]],
  ["Equipment and conveying", ["11", "13", "14", "40", "41", "42", "43", "44", "46", "48"]],
  ["Site improvements", ["32", "34", "35"]]
]
// The register's three list sizes (Phelipe 2026-10-16), following the usual
// action / informational / closeout split of submittals:
//   Essentials — what holds up the work: high-priority shop drawings, product
//     data, samples, mix designs, plans and schedules, plus long-lead items
//     and anything tied to a Schedule task.
//   Important — Essentials plus the rest of the high-priority items and the
//     medium ones that aren't routine paperwork.
//   Full — everything read.
const ACTION_TYPES = ["Shop drawings", "Product data", "Samples", "Mix design", "Plan", "Schedule"]
const PAPER_TYPES = ["Certificate", "Test report", "Qualifications", "Closeout", "Warranty"]
const LIST_SIZES = [
  ["full", "Full list", "Everything the specs and drawings ask for."],
  ["important", "Important", "Leaves out low-priority items and routine paperwork (certificates, test reports, qualifications, closeout, warranties)."],
  ["essentials", "Essentials", "Only what holds up the work: high-priority shop drawings, product data, samples, mix designs and plans, long-lead items, and anything tied to the Schedule."]
]

export default class extends Controller {
  static targets = [
    "modal", "title", "subtitle",
    "paneFiles", "dropZone", "fileInput", "fileChips", "modeSeg",
    "paneRegister",
    "paneText", "textInput", "dictateBtn", "dictateLabel", "modeSegText",
    "paneReading", "readingText", "readingProgress", "readingCount", "readingBar", "readingList",
    "paneReview", "reviewSummary", "reviewNotes", "reviewBody",
    "paneDone", "doneText", "paneError", "errorText",
    "footerNote", "backBtn", "primaryBtn", "hoverCard", "panel", "rowEditor", "rowEditorBody", "reviewBar", "reviewSearch", "reviewSearchCount", "reviewSize", "reviewAddBtn", "assignPicker", "assignPickerBody",
    "regBrowser", "regBrowseSearch", "regBrowseList", "regBrowseCount", "regPreviewTitle", "regPreviewEmpty", "regPreviewLoading", "regPreviewFrame",
    "regSummary", "regAddLabel", "regAddChevron", "regAddBody",
    "regDropZone", "regFileInput", "regFileChips",
    "regSources", "regUseSpecs", "regUseSheets", "regUseSpecsRow", "regUseSheetsRow", "regUseSpecsCount", "regUseSheetsCount"
  ]

  static values = {
    // Submittals and RFIs are US / Canada only: elsewhere no Package column,
    // "Due" instead of "Submit by", no questions turned into RFIs.
    formal: { type: Boolean, default: true },
    createUrl: String, showUrl: String, applyUrl: String, presignUrl: String,
    sectionsUrl: String, specsUrl: String, parseUrl: String, sheetsUrl: String
  }

  connect() {
    this._way = null
    this._mode = "each"
    this._files = []
    this._sections = null
    this._picked = new Set()
    this._sheets = null
    this._pickedSheets = new Set()
    this._regFiles = []
    this._regInit = false
    this._rows = []
    this._lookups = null
    this._groups = {}
    this._editing = null
    this._onKeydown = (e) => {
      if (e.key !== "Escape") return
      if (this._browseOpen) { this.closeRegisterBrowser(); return } // the register's picker first
      if (this._assignOpen) { this.closeAssign(); return } // the Assign to pop-up first
      if (this._editorOpen) { this.closeRowEditor(); return } // the row's pop-up first, then the window
      if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) this.close()
    }
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    this._stopPoll()
    this._stopDictation()
  }

  // ── Opening ───────────────────────────────────────────────────────────

  openFiles() { this._open("files") }
  openRegister() { this._open("register") }
  openText() { this._open("text") }

  _open(way) {
    document.dispatchEvent(new CustomEvent("dropdown:close", { detail: {} }))
    this._way = way
    this._pane = way
    this._import = null
    this._rows = []
    this._groups = {}
    this._editing = null
    this.modalTarget.classList.remove("hidden")
    this._showPane(way)
    if (way === "register") this._initRegister()
    this._paintFooter()
  }

  close() {
    this._stopPoll()
    this._stopDictation()
    this.modalTarget.classList.add("hidden")
    this._hideHover()
  }

  closeFromBackdrop(e) { if (e.target === this.modalTarget) this.close() }
  stopPropagation(e) { e.stopPropagation() }

  back() {
    if (this._pane === "review" || this._pane === "error") { this._showPane(this._way); this._paintFooter() }
  }

  _showPane(name) {
    this._pane = name
    const panes = { files: "paneFiles", register: "paneRegister", text: "paneText", reading: "paneReading", review: "paneReview", done: "paneDone", error: "paneError" }
    Object.entries(panes).forEach(([key, target]) => {
      const el = this[`${target}Target`]
      if (el) el.classList.toggle("hidden", key !== name)
    })
    const titles = {
      files: ["Upload files or a list", "Drop as many files as you like. Documents become items with the details filled in; lists and photos of notes become many items."],
      register: ["Build the submittal log", "From this project's specifications."],
      text: ["Paste or dictate a list", "One item per line."],
      reading: ["Reading…", ""],
      review: ["Check the items", "Fix anything highlighted, untick what you do not want. Nothing is created until you save."],
      done: ["Done", ""],
      error: ["Something went wrong", ""]
    }
    const t = titles[name] || titles[this._way] || ["", ""]
    if (name === "reading" || name === "done" || name === "error") t[0] = (titles[this._way] || [""])[0]
    // "Check the items" gets almost the whole page (Phelipe 2026-10-15).
    if (this.hasPanelTarget) {
      const big = name === "review"
      this.panelTarget.classList.toggle("max-w-6xl", !big)
      this.panelTarget.classList.toggle("max-h-[92vh]", !big)
      this.panelTarget.classList.toggle("max-w-[98vw]", big)
      this.panelTarget.classList.toggle("h-[95vh]", big)
    }
    if (name !== "review") this._closeEditorQuietly()
    this.titleTarget.textContent = t[0]
    this.subtitleTarget.textContent = t[1]
  }

  _paintFooter() {
    const btn = this.primaryBtnTarget
    this.backBtnTarget.classList.toggle("hidden", !(this._pane === "review" || this._pane === "error"))
    let label = "", enabled = false, note = ""
    switch (this._pane) {
      case "files":
        label = "Read the files"
        enabled = this._files.some(f => f.status === "done") && !this._files.some(f => f.status === "uploading")
        note = this._files.length ? `${this._files.filter(f => f.status === "done").length} of ${this._files.length} file(s) ready` : ""
        break
      case "register":
        label = "Create the Log" // Phelipe 2026-10-15: the list of submittals the project needs ("Submittal Log", 10-16)
        {
          const dropped = this._regFiles.filter(f => f.status === "done").length
          const uploading = this._regFiles.some(f => f.status === "uploading")
          const secs = this._regSpecIds().length, sheets = this._regSheetIds().length
          enabled = !uploading && (secs > 0 || sheets > 0 || dropped > 0)
          note = [secs ? `${secs} section(s)` : "", sheets ? `${sheets} sheet(s)` : "", dropped ? `${dropped} file(s)` : ""].filter(Boolean).join(" + ")
          if (uploading) note = "Uploading…"
        }
        break
      case "text":
        label = "Check the items"
        enabled = true
        break
      case "review": {
        const s = this._summarize()
        label = s.toSave === 1 ? "Save 1 action item" : `Save ${s.toSave} action items`
        enabled = s.toSave > 0
        note = s.text
        break
      }
      case "done":
        label = "Show them"
        enabled = true
        break
      default:
        label = "Read"
        enabled = false
    }
    btn.textContent = label
    btn.disabled = !enabled
    this.footerNoteTarget.textContent = note
  }

  primary() {
    switch (this._pane) {
      case "files":    return this._startImport({ source: "files", mode: this._mode, files: this._files.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.filename, content_type: f.content_type, byte_size: f.byte_size })) })
      case "register": return this._startImport({ source: "register", specification_ids: this._regSpecIds(), drawing_numbers: this._regSheetIds(),
                                                   files: this._regFiles.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.filename, content_type: f.content_type, byte_size: f.byte_size })) })
      case "text":     return this._startImport({ source: "text", mode: this._mode, text: this.textInputTarget.value })
      case "review":   return this._apply()
      case "done":
        this.close()
        window.location.reload()
        return
      default: return
    }
  }

  // ── Files: presign → PUT, chips ───────────────────────────────────────

  // The files pane and the register (Phelipe 2026-10-15) each have their
  // own drop box, chips and list; the open pane decides which one.
  _fileBox() {
    if (this._pane === "register" && this.hasRegDropZoneTarget) {
      return { list: this._regFiles, chips: this.regFileChipsTarget, zone: this.regDropZoneTarget, input: this.regFileInputTarget, max: 10 }
    }
    return { list: this._files, chips: this.fileChipsTarget, zone: this.dropZoneTarget, input: this.fileInputTarget, max: 25 }
  }

  pickFiles() { this._fileBox().input.click() }
  filesPicked(e) { this._addFiles(e.currentTarget.files); e.currentTarget.value = "" }
  dragOver(e) { e.preventDefault(); this._fileBox().zone.classList.add("border-brand", "bg-brand/5") }
  dragLeave(e) { e.preventDefault(); this._fileBox().zone.classList.remove("border-brand", "bg-brand/5") }
  drop(e) {
    e.preventDefault()
    this._fileBox().zone.classList.remove("border-brand", "bg-brand/5")
    this._addFiles(e.dataTransfer?.files)
  }

  async _addFiles(fileList) {
    const box = this._fileBox()
    const files = Array.from(fileList || []).slice(0, box.max - box.list.length)
    if (!files.length) return
    const entries = files.map(file => ({ file, filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size, status: "uploading", cache_name: null }))
    box.list.push(...entries)
    this._paintChips()
    this._paintFooter()
    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ files: entries.map(e => ({ filename: e.filename, content_type: e.content_type, byte_size: e.byte_size })) })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Couldn't start the upload.")
      }
      const { uploads } = await presignRes.json()
      await Promise.all(uploads.map(async (up, i) => {
        const entry = entries[i]
        try {
          const res = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": entry.content_type }, body: entry.file })
          if (!res.ok) throw new Error("upload")
          entry.cache_name = up.cache_name
          entry.status = "done"
        } catch (_) {
          entry.status = "failed"
        }
        this._paintChips()
        this._paintFooter()
      }))
    } catch (err) {
      entries.forEach(e => { e.status = "failed" })
      this._paintChips()
      this._paintFooter()
      this._toast(err.message || "Couldn't upload.", "alert")
    }
  }

  removeFile(e) {
    const i = Number(e.currentTarget.dataset.index)
    this._fileBox().list.splice(i, 1)
    this._paintChips()
    this._paintFooter()
  }

  _paintChips() {
    const box = this._fileBox()
    box.chips.innerHTML = box.list.map((f, i) => {
      const tone = f.status === "uploading" ? "bg-blue-50 border-blue-200 text-blue-700" : f.status === "failed" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-gray-100 border-gray-200 text-gray-700"
      const icon = f.status === "uploading" ? `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>` : `<span class="text-[10px] font-bold uppercase">${this._esc((f.filename.split(".").pop() || "").slice(0, 4))}</span>`
      return `<span class="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md border text-[13px] ${tone}">${icon}<span class="max-w-[220px] truncate">${this._esc(f.filename)}</span>${f.status === "failed" ? `<span class="text-[11px]">failed</span>` : ""}<button type="button" data-index="${i}" data-action="click->action-item-import#removeFile" class="p-0.5 rounded hover:bg-black/10 cursor-pointer" title="Remove">✕</button></span>`
    }).join("")
  }

  pickMode(e) {
    this._mode = e.currentTarget.dataset.mode === "one" ? "one" : "each"
    ;[this.hasModeSegTarget ? this.modeSegTarget : null, this.hasModeSegTextTarget ? this.modeSegTextTarget : null].filter(Boolean).forEach(seg => {
      seg.querySelectorAll("button").forEach(b => {
        const on = b.dataset.mode === this._mode
        b.classList.toggle("bg-brand/10", on); b.classList.toggle("text-brand", on); b.classList.toggle("font-semibold", on); b.classList.toggle("text-gray-500", !on)
      })
    })
  }

  // ── Register: spec sections ───────────────────────────────────────────

  async _loadSections() {
    if (this._sections) return
    try {
      const res = await fetch(this.sectionsUrlValue, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      const data = await res.json()
      this._sections = (Array.isArray(data) ? data : []).map(s => ({ id: s.id, label: s.label || s.id, spec_id: s.spec_id, preview_url: s.preview_url }))
    } catch (_) {
      this._sections = []
    }
  }

  // sections_for_select answers {id: number, label}; the import wants the
  // Specification row id, so we ask for it when the endpoint gives it and
  // otherwise fall back to the number (the server resolves either).
  _sectionKey(s) { return String(s.spec_id || s.id) }

  // ── Register: what the project has, read by default ─────────────────────
  // Specs and drawings already uploaded are used without picking (the first
  // 60 sections; the notes & schedules sheets, else the first 20 sheets).
  // The count next to each source opens its picker; anything missing is
  // dropped in the thin box below.

  // Extract from the specs / the drawings (Phelipe 2026-10-16): an unticked box
  // leaves that whole source out; its picks stay for when it's ticked again.
  _regSpecIds() { return this._useSpecs === false ? [] : Array.from(this._picked) }
  _regSheetIds() { return this._useSheets === false ? [] : Array.from(this._pickedSheets) }

  toggleRegisterSource() {
    if (this.hasRegUseSpecsTarget) this._useSpecs = this.regUseSpecsTarget.checked
    if (this.hasRegUseSheetsTarget) this._useSheets = this.regUseSheetsTarget.checked
    this._paintRegisterSummary()
    this._paintFooter()
  }

  async _initRegister() {
    await Promise.all([this._loadSections(), this._loadSheets()])
    if (!this._regInit) {
      this._regInit = true
      this._useSpecs = true
      this._useSheets = true
      ;(this._sections || []).slice(0, 60).forEach(sec => this._picked.add(this._sectionKey(sec)))
      const notes = (this._sheets || []).filter(sh => sh.notes)
      ;(notes.length ? notes : (this._sheets || [])).slice(0, 20).forEach(sh => this._pickedSheets.add(sh.id))
      const nothing = !(this._sections || []).length && !(this._sheets || []).length
      if (nothing) this._setRegisterAdd(true)
    }
    this._paintRegisterSummary()
    this._paintFooter()
  }

  _paintRegisterSummary() {
    if (!this.hasRegSummaryTarget || this._sections === null || this._sheets === null) return
    const secs = (this._sections || []).length
    const sheets = (this._sheets || []).length
    const pickedSecs = this._regSpecIds().length
    const pickedSheets = this._regSheetIds().length
    // The two boxes: shown for what the project has, with its count.
    if (this.hasRegSourcesTarget) {
      // style, not the hidden class: these carry flex classes that would win over it.
      this.regSourcesTarget.style.display = secs || sheets ? "flex" : "none"
      this.regUseSpecsRowTarget.style.display = secs ? "" : "none"
      this.regUseSheetsRowTarget.style.display = sheets ? "" : "none"
      this.regUseSpecsTarget.checked = this._useSpecs !== false
      this.regUseSheetsTarget.checked = this._useSheets !== false
      this.regUseSpecsCountTarget.textContent = `${this._picked.size} of ${secs} section${secs === 1 ? "" : "s"}`
      this.regUseSheetsCountTarget.textContent = `${this._pickedSheets.size} of ${sheets} sheet${sheets === 1 ? "" : "s"}`
    }
    const found = []
    if (secs) found.push(`specs (${secs} section${secs === 1 ? "" : "s"})`)
    if (sheets) found.push(`drawings (${sheets} sheet${sheets === 1 ? "" : "s"})`)
    let text
    if (!found.length) {
      text = "No specs or drawings are uploaded on this project yet. Drop them below and ConsLog AI will read them for the submittal log."
    } else {
      const reads = []
      if (secs && this._useSpecs === false) reads.push("none of the specs")
      if (pickedSecs) reads.push(pickedSecs === secs ? `all ${secs} spec section${secs === 1 ? "" : "s"}` : `${pickedSecs} of ${secs} spec sections`)
      if (pickedSheets) reads.push(`${pickedSheets} of ${sheets} drawing sheet${sheets === 1 ? "" : "s"}${(this._sheets || []).some(sh => sh.notes) && pickedSheets <= 20 ? " (notes & schedules)" : ""}`)
      if (sheets && this._useSheets === false) reads.push("none of the drawings")
      text = `We found your ${found.join(" and ")}. ConsLog AI will read ${reads.length ? reads.join(" and ") : "nothing yet — pick what to read below"}.`
      if (secs > 60 && pickedSecs === 60 && this._useSpecs !== false) text += " That's the most it reads at once — click the count below to choose which."
      if (!secs) text += " No specs are uploaded yet."
      if (!sheets) text += " No drawings are uploaded yet."
    }
    this.regSummaryTarget.textContent = text
    if (this.hasRegAddLabelTarget) {
      this.regAddLabelTarget.textContent = !found.length ? "Add your specs and drawings"
        : !secs ? "Missing specs? Add them here"
        : !sheets ? "Missing drawings? Add them here"
        : "Anything missing? Add files here"
    }
  }

  toggleRegisterAdd() { this._setRegisterAdd(this.regAddBodyTarget.classList.contains("hidden")) }

  _setRegisterAdd(open) {
    if (!this.hasRegAddBodyTarget) return
    this.regAddBodyTarget.classList.toggle("hidden", !open)
    if (this.hasRegAddChevronTarget) this.regAddChevronTarget.textContent = open ? "▴" : "▾"
  }

  // ── Register: drawing sheets (Phelipe 2026-10-15) ─────────────────────
  // General notes, keyed notes and schedules ask for submittals too. Up to
  // 20 sheets; "Pick notes & schedules" takes the sheets whose name says so.

  async _loadSheets() {
    if (this._sheets) return
    try {
      const res = await fetch(this.sheetsUrlValue, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      const data = await res.json()
      this._sheets = Array.isArray(data) ? data : []
    } catch (_) {
      this._sheets = []
    }
  }

  // ── Register: the picker pop-up (Phelipe 2026-10-16) ──────────────────
  // "9 of 9 sections" / "5 of 6 sheets" open a big pop-up in the look of the
  // action item's "Select drawings & specs": the list on the left (the box
  // ticks it), the actual PDF on the right (the row shows it). The ticks are
  // the picks the read uses; ticking one turns its source back on.

  openRegisterBrowser(e) {
    this._browseKind = e.currentTarget.dataset.kind === "sheets" ? "sheets" : "specs"
    this._browseStart = this._browseSet().size
    this.regBrowseSearchTarget.value = ""
    this.regBrowserTarget.querySelectorAll("[data-browse-kind]").forEach(el => { el.style.display = el.dataset.browseKind === this._browseKind ? "" : "none" })
    this._resetRegPreview()
    this.regBrowserTarget.style.display = "flex"
    this._browseOpen = true
    this._renderRegBrowser()
    this.regBrowseListTarget.scrollTop = 0
    this.regBrowseSearchTarget.focus()
  }

  closeRegisterBrowser() {
    if (!this._browseOpen) return
    this._browseOpen = false
    this.regBrowserTarget.style.display = "none"
    this._resetRegPreview()
    const picked = this._browseSet().size
    if (picked !== this._browseStart) track("Submittal Log Sources Picked", { source: this._browseKind, picked, available: this._browseItems().length })
    this._paintRegisterSummary()
    this._paintFooter()
  }

  closeRegisterBrowserBackdrop(e) { if (e.target === e.currentTarget) this.closeRegisterBrowser() }
  filterRegisterBrowser() { this._renderRegBrowser() }

  _browseItems() { return (this._browseKind === "sheets" ? this._sheets : this._sections) || [] }
  _browseKey(item) { return this._browseKind === "sheets" ? String(item.id) : this._sectionKey(item) }
  _browseSet() { return this._browseKind === "sheets" ? this._pickedSheets : this._picked }
  _browseMax() { return this._browseKind === "sheets" ? 20 : 60 } // what one read takes
  _browseNoun(n) { return this._browseKind === "sheets" ? (n === 1 ? "sheet" : "sheets") : (n === 1 ? "section" : "sections") }
  _browseShown() {
    const q = this.regBrowseSearchTarget.value.trim().toLowerCase()
    return this._browseItems().filter(it => !q || String(it.label).toLowerCase().includes(q) || String(it.id).toLowerCase().includes(q))
  }

  _sourceOn() {
    if (this._browseKind === "sheets") this._useSheets = true; else this._useSpecs = true
  }

  _renderRegBrowser() {
    if (!this._browseOpen) return
    const set = this._browseSet(), all = this._browseItems()
    const tick = `<svg class="w-5 h-5 text-brand shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
    const box = `<div class="w-5 h-5 shrink-0 rounded border border-gray-300"></div>`
    this.regBrowseListTarget.innerHTML = this._browseShown().map(it => {
      const key = this._browseKey(it), on = set.has(key), active = this._browsePreviewKey === key
      return `<div data-key="${this._esc(key)}" data-action="click->action-item-import#previewRegisterItem"
                   class="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${active ? "bg-gray-100" : on ? "bg-brand/5" : ""}">
        <span data-key="${this._esc(key)}" data-action="click->action-item-import#toggleRegisterItem" class="shrink-0 p-1 -m-1 rounded hover:bg-brand/10">${on ? tick : box}</span>
        <span class="text-sm flex-1 min-w-0 truncate ${on ? "text-brand font-medium" : "text-gray-700"}" title="${this._esc(it.label)}">${this._esc(it.label)}</span>
        ${it.notes ? `<span class="shrink-0 text-[13px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">Notes</span>` : ""}
      </div>`
    }).join("") || `<div class="px-3 py-10 text-[13px] text-gray-400 text-center">${all.length ? "No matches" : "Nothing here yet"}</div>`
    this.regBrowseCountTarget.textContent = `${set.size} of ${all.length} ${this._browseNoun(all.length)} picked · up to ${this._browseMax()} at a time`
  }

  toggleRegisterItem(e) {
    e.stopPropagation()
    const key = e.currentTarget.dataset.key, set = this._browseSet()
    if (set.has(key)) {
      set.delete(key)
    } else {
      if (set.size >= this._browseMax()) { this._toast(`Up to ${this._browseMax()} ${this._browseNoun(2)} at a time.`, "alert"); return }
      set.add(key)
      this._sourceOn()
    }
    this._renderRegBrowser()
  }

  // Pick all: everything the search shows, up to what one read takes.
  pickAllRegister() {
    const set = this._browseSet(), max = this._browseMax()
    const shown = this._browseShown().map(it => this._browseKey(it)).filter(k => !set.has(k))
    const room = Math.max(0, max - set.size)
    shown.slice(0, room).forEach(k => set.add(k))
    if (set.size) this._sourceOn()
    if (shown.length > room) this._toast(`${max} picked — that's the most one read takes.`, "alert")
    this._renderRegBrowser()
  }

  clearRegisterBrowse() { this._browseSet().clear(); this._renderRegBrowser() }

  pickNotesSheets() {
    const notes = (this._sheets || []).filter(s => s.notes && !this._pickedSheets.has(String(s.id)))
    if (!notes.length && !(this._sheets || []).some(s => s.notes)) { this._toast("No sheet here is named as notes or schedules — pick them by hand.", "alert"); return }
    notes.slice(0, Math.max(0, 20 - this._pickedSheets.size)).forEach(s => this._pickedSheets.add(String(s.id)))
    if (this._pickedSheets.size) this._useSheets = true
    this._renderRegBrowser()
  }

  // The PDF pane: fetch the presigned file and hand the browser's own PDF
  // viewer a blob: URL, the same way the action item's pop-up does.
  async previewRegisterItem(e) {
    const key = e.currentTarget.dataset.key
    const it = this._browseItems().find(x => this._browseKey(x) === key)
    if (!it) return
    this._browsePreviewKey = key
    this._renderRegBrowser()
    this.regPreviewTitleTarget.textContent = it.label
    this.regPreviewTitleTarget.style.display = ""
    if (!it.preview_url) { this._regPreviewState("empty"); return }
    this._regPreviewState("loading")
    try {
      const resp = await fetch(it.preview_url)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = new Blob([await resp.arrayBuffer()], { type: "application/pdf" })
      if (this._browsePreviewKey !== key || !this._browseOpen) return // a newer click won
      if (this._regPreviewUrl) URL.revokeObjectURL(this._regPreviewUrl)
      this._regPreviewUrl = URL.createObjectURL(blob)
      this.regPreviewFrameTarget.src = this._regPreviewUrl
      this._regPreviewState("frame")
    } catch (_) {
      if (this._browsePreviewKey === key) this._regPreviewState("empty")
    }
  }

  _regPreviewState(state) {
    this.regPreviewEmptyTarget.style.display = state === "empty" ? "flex" : "none"
    this.regPreviewLoadingTarget.style.display = state === "loading" ? "flex" : "none"
    this.regPreviewFrameTarget.style.display = state === "frame" ? "block" : "none"
  }

  _resetRegPreview() {
    this._browsePreviewKey = null
    if (this._regPreviewUrl) { URL.revokeObjectURL(this._regPreviewUrl); this._regPreviewUrl = null }
    if (!this.hasRegPreviewFrameTarget) return
    this.regPreviewFrameTarget.removeAttribute("src")
    this.regPreviewTitleTarget.style.display = "none"
    this._regPreviewState("empty")
  }

  // ── Dictate into the list ─────────────────────────────────────────────

  dictate() {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Rec) { this._toast("Dictation isn't available in this browser — type or paste the lines instead.", "alert"); return }
    if (this._rec) { this._stopDictation(); return }
    const rec = new Rec()
    rec.lang = document.documentElement.lang || "en-US"
    rec.continuous = true
    rec.interimResults = false
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (!ev.results[i].isFinal) continue
        const said = ev.results[i][0].transcript.trim()
        if (!said) continue
        const parts = said.split(/\b(?:new line|next item|next line|next|period)\b/i).map(s => s.trim()).filter(Boolean)
        const ta = this.textInputTarget
        ta.value = (ta.value ? ta.value.replace(/\s+$/, "") + "\n" : "") + parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join("\n")
      }
    }
    rec.onerror = () => this._stopDictation()
    rec.onend = () => { if (this._rec) this._stopDictation() }
    this._rec = rec
    rec.start()
    this.dictateLabelTarget.textContent = "Listening… click to stop"
    this.dictateBtnTarget.classList.add("text-rose-600", "border-rose-300")
  }

  _stopDictation() {
    const rec = this._rec
    this._rec = null
    try { rec && rec.stop() } catch (_) { /* already stopped */ }
    if (this.hasDictateLabelTarget) this.dictateLabelTarget.textContent = "Dictate"
    if (this.hasDictateBtnTarget) this.dictateBtnTarget.classList.remove("text-rose-600", "border-rose-300")
  }

  // ── The read: start, poll ─────────────────────────────────────────────

  async _startImport(body) {
    this._showPane("reading")
    if (this.hasReadingProgressTarget) { this.readingProgressTarget.classList.add("hidden"); this.readingListTarget.innerHTML = "" }
    const sheetsToo = body.source === "register" && (body.drawing_numbers || []).length
    this.readingTextTarget.textContent = body.source === "register" ? (sheetsToo ? "ConsLog AI is reading the spec sections and drawing sheets…" : "ConsLog AI is reading the spec sections…") : body.source === "text" ? "Sorting the lines…" : "ConsLog AI is reading the files…"
    this._paintFooter()
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Couldn't start the read.")
      this._import = data.id
      this._poll()
    } catch (err) {
      this._showError(err.message)
    }
  }

  _poll() {
    this._stopPoll()
    const tick = async () => {
      if (!this._import) return
      try {
        const res = await fetch(this.showUrlValue.replace("IMPORT_ID", this._import), { credentials: "same-origin", headers: { "Accept": "application/json" } })
        const data = await res.json()
        if (data.options && data.options.progress) this._paintProgress(data.options.progress)
        if (data.status === "analyzed" || data.status === "done") { this._showReview(data); return }
        if (data.status === "failed") { this._showError(data.error || "ConsLog AI couldn't read that."); return }
      } catch (_) { /* keep polling */ }
      this._pollTimer = setTimeout(tick, 2500)
    }
    this._pollTimer = setTimeout(tick, 1500)
  }

  _stopPoll() { if (this._pollTimer) { clearTimeout(this._pollTimer); this._pollTimer = null } }

  // What's being read (Phelipe 2026-10-15: "show what's loading"): one line
  // per spec section and drawing sheet — waiting, reading, what it found or
  // that it couldn't be read — a bar, the running count of submittals, then
  // the checking step (types, related drawings, packages, dates).
  _paintProgress(p) {
    if (!this.hasReadingProgressTarget) return
    const steps = Array.isArray(p.steps) ? p.steps : []
    if (!steps.length) return
    const finished = steps.filter(s => s.state === "done" || s.state === "failed").length
    const found = steps.reduce((n, s) => n + (Number(s.found) || 0), 0)
    const checking = p.phase === "checking"
    const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`
    this.readingProgressTarget.classList.remove("hidden")
    this.readingCountTarget.textContent = checking
      ? `All ${steps.length} read · ${plural(Number(p.found ?? found), "submittal")} found`
      : `${finished} of ${steps.length} read · ${plural(found, "submittal")} found so far`
    this.readingBarTarget.style.width = `${checking ? 100 : Math.round((finished / steps.length) * 100)}%`
    if (checking) this.readingTextTarget.textContent = "Checking types, related drawings, packages and dates…"
    const spin = `<svg class="w-4 h-4 animate-spin text-brand" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
    const icon = {
      waiting: `<span class="w-3 h-3 rounded-full border border-gray-300"></span>`,
      reading: spin,
      done: `<span class="text-green-600 font-bold">✓</span>`,
      failed: `<span class="text-amber-600 font-bold">!</span>`
    }
    const right = (s) => s.state === "done" ? (Number(s.found) ? plural(Number(s.found), "submittal") : "None")
      : s.state === "failed" ? "Couldn't read" : s.state === "reading" ? "Reading…" : "Waiting"
    this.readingListTarget.innerHTML = steps.map(s => `<li class="flex items-center gap-2.5 px-3 py-1.5 text-[13px] ${s.state === "waiting" ? "text-gray-400" : "text-gray-800"}">
        <span class="w-4 h-4 flex items-center justify-center shrink-0">${icon[s.state] || icon.waiting}</span>
        <span class="shrink-0 rounded px-1.5 py-px border border-gray-200 bg-gray-50 text-gray-500">${s.kind === "sheet" ? "Sheet" : "Spec"}</span>
        <span class="flex-1 min-w-0 truncate" title="${this._esc(s.label)}">${this._esc(s.label)}</span>
        <span class="shrink-0 tabular-nums ${s.state === "failed" ? "text-amber-700" : s.state === "done" && Number(s.found) ? "text-gray-900 font-medium" : "text-gray-400"}">${right(s)}</span>
      </li>`).join("")
  }

  _showError(message) {
    this._stopPoll()
    this._showPane("error")
    this.errorTextTarget.textContent = message
    this._paintFooter()
  }

  // ── Review ────────────────────────────────────────────────────────────

  _showReview(data) {
    this._rows = (data.rows || []).map(r => ({ ...r, include: r.include !== false, save_as: r.save_as || "draft", priority: ["low", "medium", "high"].includes(r.priority) ? r.priority : "low", spec_drawings: r.spec_drawings || [], external_emails: r.external_emails || [], flags: r.flags || [] }))
    this._lookups = data.lookups || { kinds: [], members: [], packages: [], catalog: [] }
    this._files = (data.files || []).map(f => ({ ...f, status: "done" }))
    this._source = data.source
    this._order = "construction"
    this._query = ""
    this._size = "full"
    // Worked out once, so editing a row's priority doesn't make it vanish.
    this._rows.forEach(r => { r.tier = this._tierOf(r) })
    if (this.hasReviewSearchTarget) this.reviewSearchTarget.value = ""
    const defaultMode = (data.options && data.options.mode) || this._mode
    this._groups = {}
    this._fileKeys().forEach(key => {
      const rows = this._rows.filter(r => this._fileKey(r) === key)
      const first = rows[0] || {}
      const listLike = data.source !== "register" && (first.document_kind === "notes" || first.document_kind === "log" || first.source_row || data.source === "text")
      this._groups[key] = {
        mode: listLike ? defaultMode : "each",
        canOne: data.source !== "register",
        kind_id: first.default_kind_id || first.kind_id || null,
        title: "", assignee_email: "", due_date: "", save_as: "draft"
      }
      if (this._groups[key].mode === "one") this._applyOneDefaults(key)
    })
    this._showPane("review")
    this._renderReview()
    this._paintFooter()
  }

  _fileKey(r) { return r.file === null || r.file === undefined ? "none" : String(r.file) }
  _fileKeys() { return Array.from(new Set(this._rows.map(r => this._fileKey(r)))) }
  _fileName(key) {
    if (key === "none") { const r = this._rows.find(x => this._fileKey(x) === "none"); return r?.file_name || "List" }
    return (this._files[Number(key)] || {}).filename || `File ${Number(key) + 1}`
  }

  // His defaults (2026-10-13): one item each starts as the submittal type,
  // all-in-one as the punchlist type.
  _kindByName(re) { return (this._lookups.kinds || []).find(k => re.test(k.name || "")) }

  _applyOneDefaults(key) {
    const g = this._groups[key]
    const punch = this._kindByName(/punch|checklist/i)
    if (punch) g.kind_id = punch.id
    const rows = this._rows.filter(r => this._fileKey(r) === key)
    if (!g.title) {
      const name = this._fileName(key).replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim()
      g.title = name && name !== "List" ? `Checklist: ${name}` : `Checklist ${new Date().toLocaleDateString()}`
    }
    const withPerson = rows.find(r => r.assignee_email)
    if (!g.assignee_email && withPerson) g.assignee_email = withPerson.assignee_email
  }

  _renderReview() {
    const kinds = this._lookups.kinds || []
    const members = this._lookups.members || []
    const packages = Array.from(new Set((this._lookups.packages || []).map(p => p.name).concat(this._rows.map(r => r.package).filter(Boolean))))
    const catalog = this._lookups.catalog || []
    const kindOptions = (sel) => kinds.map(k => `<option value="${k.id}" ${String(k.id) === String(sel || "") ? "selected" : ""}>${this._esc(k.name)}</option>`).join("")
    const memberOptions = (sel) => `<option value="">Choose…</option>` + members.map(m => `<option value="${this._esc(m.email)}" ${(m.email || "").toLowerCase() === String(sel || "").toLowerCase() ? "selected" : ""}>${this._esc(m.name || m.email)}</option>`).join("")
    const html = []
    html.push(`<datalist id="aii-packages">${packages.map(p => `<option value="${this._esc(p)}"></option>`).join("")}</datalist>`)
    html.push(`<datalist id="aii-catalog">${catalog.map(c => `<option value="${this._esc(c.id)}">${this._esc(c.label)}</option>`).join("")}</datalist>`)
    html.push(`<datalist id="aii-sections">${catalog.filter(c => c.kind === "spec").map(c => `<option value="${this._esc(c.id)}">${this._esc(c.label)}</option>`).join("")}</datalist>`)

    if (this.hasReviewBarTarget) this.reviewBarTarget.classList.toggle("hidden", !this._isRegister())
    if (this.hasReviewAddBtnTarget) this.reviewAddBtnTarget.style.display = this._isRegister() ? "inline-flex" : "none"
    if (this._isRegister()) {
      this._renderRegister(html, kindOptions, memberOptions)
      if (this._noMatches()) html.push(this._noMatchRow())
      this.reviewBodyTarget.innerHTML = html.join("")
      this._paintSummary()
      if (this._editorOpen) this._paintEditor()
      return
    }
    this._fileKeys().forEach(key => {
      const g = this._groups[key]
      const rows = this._rows.map((r, i) => [r, i]).filter(([r]) => this._fileKey(r) === key)
      const first = rows[0] ? rows[0][0] : {}
      const what = first.document_kind === "package" ? `split into ${rows.length} submittal${rows.length === 1 ? "" : "s"}` : `${rows.length} ${rows.length === 1 ? "item" : "items"} read`
      const seg = g.canOne ? `<span class="inline-flex items-center gap-2 ml-auto"><span class="font-medium">These ${rows.length} ${rows.length === 1 ? "line" : "lines"}:</span>
        <span class="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">
          <button type="button" data-key="${key}" data-mode="each" data-action="click->action-item-import#pickGroupMode" class="px-2.5 py-0.5 cursor-pointer ${g.mode === "each" ? "bg-brand/10 text-brand font-semibold" : "text-gray-500"}">One action item each</button>
          <button type="button" data-key="${key}" data-mode="one" data-action="click->action-item-import#pickGroupMode" class="px-2.5 py-0.5 border-l border-gray-300 cursor-pointer ${g.mode === "one" ? "bg-brand/10 text-brand font-semibold" : "text-gray-500"}">All in one, as a checklist</button>
        </span></span>` : ""
      const typeAll = g.mode === "each" ? `<span class="inline-flex items-center gap-1.5 ${g.canOne ? "" : "ml-auto"}"><span class="text-gray-500">Type for all:</span>
        <select data-key="${key}" data-action="change->action-item-import#typeForAll" class="px-2 py-0.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none cursor-pointer"><option value="">—</option>${kindOptions("")}</select></span>` : ""
      html.push(`<tr class="bg-gray-50 border-t border-b border-gray-200"><td colspan="${this._cols()}" class="px-3 py-2">
        <div class="flex items-center gap-3 flex-wrap text-[13px] text-gray-900">
          <span class="font-semibold truncate max-w-[320px]">${this._esc(this._fileName(key))}</span>
          <span class="text-gray-500">· ${what}</span>
          ${seg}${typeAll}
        </div>
      </td></tr>`)
      if (g.mode === "one") {
        html.push(`<tr class="bg-brand/5 border-b-2 border-gray-200"><td class="px-2 py-2 text-center"><input type="checkbox" checked disabled class="rounded border-gray-300"></td>
          <td class="px-2 py-2"><select data-key="${key}" data-field="kind_id" data-action="change->action-item-import#groupField" class="px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white cursor-pointer">${kindOptions(g.kind_id)}</select></td>
          <td class="px-2 py-2"><input type="text" value="${this._esc(g.title)}" data-key="${key}" data-field="title" data-action="input->action-item-import#groupField" placeholder="Title of the one item" class="w-full min-w-[220px] px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white font-medium"></td>
          <td class="px-2 py-2 text-gray-400" colspan="2">${rows.length} lines go in as its checklist</td>
          <td class="px-2 py-2"><select data-key="${key}" data-field="assignee_email" data-action="change->action-item-import#groupField" class="px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white cursor-pointer max-w-[170px]">${memberOptions(g.assignee_email)}</select></td>
          <td class="px-2 py-2"><input type="text" data-controller="datepicker" value="${this._esc(g.due_date)}" data-key="${key}" data-field="due_date" data-action="change->action-item-import#groupField" placeholder="Due" class="w-28 px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white"></td>
          <td class="px-2 py-2 text-gray-400">—</td>
          <td class="px-2 py-2">${this._saveAsToggle(g.save_as, `data-key="${key}" data-action="click->action-item-import#groupSaveAs"`)}</td>
          ${this.formalValue ? `<td class="px-2 py-2 text-gray-400">—</td>` : ""}</tr>`)
      }
      rows.filter(([r]) => this._rowShown(r)).forEach(([r, i]) => html.push(this._rowHtml(r, i, g, kindOptions, memberOptions)))
    })
    if (this._noMatches()) html.push(this._noMatchRow())
    this.reviewBodyTarget.innerHTML = html.join("")
    this._paintSummary()
    if (this._editorOpen) this._paintEditor()
  }

  _noMatches() { return !!(this._query && this._query.trim()) && !this._rows.some(r => this._rowShown(r)) }
  _noMatchRow() { return `<tr><td colspan="${this._cols()}" class="px-3 py-6 text-center text-[13px] text-gray-500">Nothing matches “${this._esc(this._query.trim())}”.</td></tr>` }

  _cols() { return this.formalValue ? 10 : 9 }

  // ── The register's order (Phelipe 2026-10-15) ─────────────────────────

  _isRegister() { return this._source === "register" }

  _division(r) {
    const m = String(r.spec_section || "").replace(/[\s.\-]/g, "").match(/^(\d{2})\d{4}/)
    return m ? m[1] : null
  }

  _rowSection(r) { return String(r.spec_section || "").replace(/\s+/g, "") }

  // [{label, idx: [row indexes]}] in the order picked; inside a group by spec
  // section, then as read.
  _registerGroups() {
    const all = this._rows.map((r, i) => i)
    const bySection = (a, b) => this._rowSection(this._rows[a]).localeCompare(this._rowSection(this._rows[b]), undefined, { numeric: true }) || a - b
    const groups = []
    let pool = all
    let hintPrefix = ""
    if (this._order === "division") {
      const divs = Array.from(new Set(all.map(i => this._division(this._rows[i])).filter(Boolean))).sort()
      divs.forEach(d => groups.push({ label: `Division ${d} — ${DIVISIONS[d] || "Other"}`, idx: all.filter(i => this._division(this._rows[i]) === d).sort(bySection) }))
    } else {
      // By construction (Phelipe 2026-10-15: "by order of construction", Schedule
      // first): items tied to the project Schedule come in the order they're
      // needed on site, one heading per Schedule activity; the rest follow the
      // typical order a building goes up.
      const onSchedule = all.filter(i => this._rows[i].needed_on_site)
        .sort((a, b) => String(this._rows[a].needed_on_site).localeCompare(String(this._rows[b].needed_on_site)) || bySection(a, b))
      const byTask = new Map()
      onSchedule.forEach(i => {
        const r = this._rows[i]
        const key = `${r.needed_on_site}|${r.task_id || r.task_name || ""}`
        if (!byTask.has(key)) byTask.set(key, [])
        byTask.get(key).push(i)
      })
      byTask.forEach(idx => {
        const r = this._rows[idx[0]]
        groups.push({ label: `On site ${this._fmtDate(r.needed_on_site)}${r.task_name ? ` · ${r.task_name}` : ""}`, hint: "from the Schedule", idx })
      })
      pool = all.filter(i => !this._rows[i].needed_on_site)
      if (onSchedule.length) hintPrefix = "not on the Schedule · "
      const staged = new Set(STAGES.flatMap(([, ds]) => ds))
      STAGES.forEach(([name, ds]) => {
        const idx = ds.flatMap(d => pool.filter(i => this._division(this._rows[i]) === d).sort(bySection))
        if (idx.length) groups.push({ label: name, hint: hintPrefix + ds.filter(d => idx.some(i => this._division(this._rows[i]) === d)).map(d => `Div ${d}`).join(", "), idx })
      })
      const unstaged = pool.filter(i => { const d = this._division(this._rows[i]); return d && !staged.has(d) }).sort(bySection)
      if (unstaged.length) groups.push({ label: "Other divisions", hint: hintPrefix.replace(/ · $/, ""), idx: unstaged })
    }
    const other = pool.filter(i => !this._division(this._rows[i])).sort(bySection)
    if (other.length) groups.push({ label: "No spec section", hint: `${hintPrefix}drawing notes and other sections`, idx: other })
    return this._arrange(groups)
  }

  // Drag and drop (Phelipe 2026-10-16): a row dropped under another heading
  // stays there (r.pin), and a heading keeps the order its rows were dragged
  // to (r.sort) — each kept per order, so By construction and By division
  // don't undo each other.
  _arrange(groups) {
    const mode = this._order
    const byLabel = new Map(groups.map(g => [g.label, g]))
    groups.forEach(g => {
      g.idx = g.idx.filter(i => {
        const to = (this._rows[i].pin || {})[mode]
        if (!to || to === g.label || !byLabel.has(to)) return true
        byLabel.get(to).idx.push(i)
        return false
      })
    })
    groups.forEach(g => {
      if (!g.idx.some(i => (this._rows[i].sort || {})[mode] !== undefined)) return
      const pos = new Map(g.idx.map((i, n) => [i, n]))
      const key = (i) => { const v = (this._rows[i].sort || {})[mode]; return v === undefined ? pos.get(i) : v }
      g.idx.sort((a, b) => key(a) - key(b) || pos.get(a) - pos.get(b))
    })
    return groups.filter(g => g.idx.length)
  }

  // ── Add an item by hand (Phelipe 2026-10-16) ──────────────────────────
  // "+ Add an item" opens a blank row in the row's pop-up; once it has a
  // title it lands under the heading its spec section belongs to (guessed
  // from the title when none is given), or under the heading whose "+ Add
  // here" was clicked. Marked Manual, in yellow.

  addRegisterItem(e) {
    const label = e.currentTarget.dataset.group || null
    const like = this._rows.find(r => r.kind === "submittal") || this._rows[0] || {}
    const r = {
      i: null, file: like.file ?? null, file_name: like.file_name || "", manual: true, include: true,
      kind: like.kind || "submittal", kind_id: like.default_kind_id || like.kind_id || null, kind_name: like.kind_name || "Submittal",
      title: "", description: "", spec_section: "", spec_drawings: [], external_emails: [], flags: [],
      priority: "medium", save_as: "draft", tier: 1, confidence: 1, pin: label ? { [this._order]: label } : {}
    }
    this._rows.push(r)
    this._query = ""
    if (this.hasReviewSearchTarget) this.reviewSearchTarget.value = ""
    this._renderReview()
    this.editRow({ currentTarget: { dataset: { i: String(this._rows.length - 1) } } })
  }

  // Leaving a hand-added row: dropped if it never got a title, else given a
  // spec section from its title when it has neither one nor a heading.
  _settleManual(i) {
    const r = this._rows[i]
    if (!r || !r.manual || r.settled) return
    if (!String(r.title || "").trim()) {
      if (i === this._rows.length - 1) this._rows.pop()
      return
    }
    r.settled = true
    let placed = (r.pin || {})[this._order] ? "heading" : "section"
    if (!r.spec_section && placed === "section") {
      const guess = this._guessSection(`${r.title} ${r.description || ""}`)
      if (guess) {
        r.spec_section = guess
        if (!r.spec_drawings.includes(guess)) r.spec_drawings.push(guess)
        placed = "guessed"
      } else {
        placed = "none"
      }
    }
    track("Submittal Log Item Added", { placed, order: this._order, items_read: this._rows.length })
    this._scrollToRow = i
  }

  // The project's spec section whose title shares the most words with it.
  _guessSection(text) {
    const stop = new Set(["and", "the", "for", "with", "from", "data", "product", "shop", "drawing", "submittal", "plan", "report", "test", "sample", "certificate", "schedule", "system", "work", "general", "detail", "material", "installation", "requirement"])
    const words = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      .split(/[^a-z0-9]+/).map(w => w.replace(/ies$/, "y").replace(/([^s])s$/, "$1")).filter(w => w.length > 2 && !stop.has(w))
    const want = new Set(words(text))
    if (!want.size) return null
    let best = null, score = 0
    ;(this._lookups.catalog || []).filter(c => c.kind === "spec").forEach(c => {
      const n = new Set(words(String(c.label).replace(/^[\d\s.]+(—|-)?\s*/, "")).filter(w => want.has(w))).size
      if (n > score) { score = n; best = c }
    })
    return best ? String(best.id) : null
  }

  // A spec section typed or picked in the pop-up: the row goes under its heading.
  rowSection(e) {
    const i = Number(e.currentTarget.dataset.i)
    const r = this._rows[i]
    if (!r) return
    const v = e.currentTarget.value.trim()
    const known = (this._lookups.catalog || []).find(c => c.kind === "spec" && (String(c.id) === v || c.label === v))
    r.spec_section = known ? String(known.id) : v
    if (known && !r.spec_drawings.includes(String(known.id))) r.spec_drawings.push(String(known.id))
    r.pin = {}
    this._renderReview()
  }

  // ── Drag a row (Phelipe 2026-10-16) ───────────────────────────────────
  // Only from its ⋮⋮ handle, so clicking a field still opens the pop-up.
  // Dropped on a row it goes above or below it; on a heading, first in it.

  rowGrip(e) {
    const tr = e.currentTarget.closest("tr")
    if (!tr) return
    tr.draggable = true
    document.addEventListener("mouseup", () => { tr.draggable = false }, { once: true })
  }

  rowDragStart(e) {
    const tr = e.currentTarget
    if (!tr.draggable) { e.preventDefault(); return }
    this._dragI = Number(tr.dataset.row)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(this._dragI))
    this._hideHover()
    requestAnimationFrame(() => tr.classList.add("opacity-40"))
  }

  rowDragOver(e) {
    if (this._dragI == null) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "move"
    const el = e.currentTarget
    const box = el.getBoundingClientRect()
    const main = el.dataset.row !== undefined
    this._dropAfter = el.dataset.dropI === undefined ? false : (!main || e.clientY > box.top + box.height / 2)
    if (this._dropEl && this._dropEl !== el) this._dropEl.style.boxShadow = ""
    this._dropEl = el
    el.style.boxShadow = el.dataset.dropI === undefined || !this._dropAfter ? "inset 0 2px 0 #3b82f6" : "inset 0 -2px 0 #3b82f6"
  }

  rowDragLeave(e) {
    if (this._dropEl === e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) { this._dropEl.style.boxShadow = ""; this._dropEl = null }
  }

  rowDrop(e) {
    if (this._dragI == null) return
    e.preventDefault()
    e.stopPropagation()
    const from = this._dragI
    const el = e.currentTarget
    const label = el.dataset.group
    const g = this._registerGroups().find(x => x.label === label)
    this._endDrag()
    if (!g) return
    const order = g.idx.filter(i => i !== from)
    let at = 0
    if (el.dataset.dropI !== undefined) {
      const k = order.indexOf(Number(el.dataset.dropI))
      at = k < 0 ? order.length : k + (this._dropAfter ? 1 : 0)
    }
    order.splice(at, 0, from)
    const r = this._rows[from]
    const across = !g.idx.includes(from)
    r.pin = { ...(r.pin || {}), [this._order]: label }
    order.forEach((i, n) => { const x = this._rows[i]; x.sort = { ...(x.sort || {}), [this._order]: n } })
    track("Submittal Log Item Moved", { across_headings: across, manual: !!r.manual, order: this._order })
    this._renderReview()
  }

  rowDragEnd() { this._endDrag(); this._renderReview() }

  _endDrag() {
    this._dragI = null
    if (this._dropEl) { this._dropEl.style.boxShadow = ""; this._dropEl = null }
  }

  // Row indexes in the order they're shown (and saved, so the numbers follow it).
  _displayOrder() {
    if (!this._isRegister()) return this._rows.map((r, i) => i)
    return this._registerGroups().flatMap(g => g.idx)
  }

  // ── Search the items (Phelipe 2026-10-15) ─────────────────────────────
  // Every word typed has to be in the row: title, description, type,
  // drawings & specs, person, package, where it was found. Accents and
  // capitals ignored. Saving still sends every row.

  searchRows(e) {
    this._query = e.currentTarget.value
    this._renderReview()
  }

  clearRowSearch(e) {
    if (!this._query) return
    e.stopPropagation()
    this._query = ""
    this.reviewSearchTarget.value = ""
    this._renderReview()
  }

  _rowMatches(r) {
    const fold = (t) => String(t ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    const words = fold(this._query).split(/\s+/).filter(Boolean)
    if (!words.length) return true
    const kind = (this._lookups.kinds || []).find(k => String(k.id) === String(r.kind_id))
    const text = fold([r.title, r.description, kind ? kind.name : r.kind_name, (r.spec_drawings || []).join(" "), r.spec_section,
      r.assignee_name, r.assignee_email, (r.external_emails || []).join(" "), r.responsible, r.package, r.found_in, r.submittal_type].filter(Boolean).join(" "))
    return words.every(w => text.includes(w))
  }

  // The rows shown right now, in order (the pop-up's Previous / Next walk these).
  _visibleOrder() { return this._displayOrder().filter(i => this._rowShown(this._rows[i])) }
  _rowShown(r) { return this._inSize(r) && this._rowMatches(r) }

  // ── List size (Phelipe 2026-10-16) ────────────────────────────────────
  // Rows outside the size are hidden and not saved; switching back brings
  // them back as they were.

  _tierOf(r) {
    const type = r.submittal_type || "", high = r.priority === "high"
    if (r.needed_on_site || Number(r.lead_time_days) >= 28 || (high && ACTION_TYPES.includes(type))) return 1
    if (high || (r.priority === "medium" && !PAPER_TYPES.includes(type))) return 2
    return 3
  }

  _inSize(r) {
    if (!this._isRegister() || this._size === "full") return true
    return (r.tier || 3) <= (this._size === "essentials" ? 1 : 2)
  }

  _sizeCount(size) {
    const max = size === "essentials" ? 1 : size === "important" ? 2 : 3
    return this._rows.filter(r => (r.tier || 3) <= max).length
  }

  setListSize(e) {
    const size = e.currentTarget.dataset.size
    if (!LIST_SIZES.some(([v]) => v === size) || size === this._size) return
    this._size = size
    track("Submittal Log List Size Changed", { list_size: size, items_shown: this._sizeCount(size), items_read: this._rows.length })
    this._renderReview()
  }

  _paintSize() {
    if (!this.hasReviewSizeTarget) return
    const el = this.reviewSizeTarget
    el.style.display = this._isRegister() ? "inline-flex" : "none"
    if (!this._isRegister()) return
    const btns = LIST_SIZES.map(([value, label, help], n) => {
      const count = this._sizeCount(value), on = this._size === value
      return `<button type="button" data-size="${value}" data-action="click->action-item-import#setListSize" title="${this._esc(help)}" ${count ? "" : "disabled"}
        class="px-2.5 py-1 whitespace-nowrap ${n ? "border-l border-gray-300" : ""} ${on ? "bg-brand/10 text-brand font-semibold" : "text-gray-600 hover:bg-gray-50"} ${count ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}">${label} <span class="${on ? "" : "text-gray-400"} tabular-nums">${count}</span></button>`
    }).join("")
    el.innerHTML = `<span class="text-gray-500">List:</span><span class="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">${btns}</span>`
  }

  setOrder(e) {
    this._order = e.currentTarget.dataset.order === "division" ? "division" : "construction"
    this._renderReview()
  }

  _renderRegister(html, kindOptions, memberOptions) {
    const seg = (value, label) => `<button type="button" data-order="${value}" data-action="click->action-item-import#setOrder" class="px-2.5 py-0.5 cursor-pointer ${this._order === value ? "bg-brand/10 text-brand font-semibold" : "text-gray-500"} ${value === "division" ? "border-l border-gray-300" : ""}">${label}</button>`
    // Above the column titles, outside the table (Phelipe 2026-10-15).
    if (this.hasReviewBarTarget) this.reviewBarTarget.innerHTML = `<div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <div class="flex items-center gap-3 flex-wrap text-[13px] text-gray-900">
        <span class="font-semibold">Submittal log</span>
        <span class="text-gray-500">· ${this._rows.filter(r => !r.manual).length} ${this._rows.filter(r => !r.manual).length === 1 ? "item" : "items"} read</span>
        <span class="inline-flex items-center gap-2 ml-auto"><span class="text-gray-500">Order:</span>
          <span class="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">${seg("construction", "By construction")}${seg("division", "By division")}</span></span>
        <span class="inline-flex items-center gap-1.5"><span class="text-gray-500">Type for all:</span>
          <select data-key="all" data-action="change->action-item-import#typeForAll" class="px-2 py-0.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none cursor-pointer"><option value="">—</option>${kindOptions("")}</select></span>
      </div>
    </div>`
    this._registerGroups().map(grp => ({ ...grp, idx: grp.idx.filter(i => this._rowShown(this._rows[i])) })).filter(grp => grp.idx.length).forEach(grp => {
      html.push(`<tr class="border-b border-gray-200" data-group="${this._esc(grp.label)}" data-action="dragover->action-item-import#rowDragOver dragleave->action-item-import#rowDragLeave drop->action-item-import#rowDrop"><td colspan="${this._cols()}" class="px-3 pt-3 pb-1.5 text-[13px]">
        <span class="flex items-center gap-1"><span><span class="font-semibold text-gray-900">${this._esc(grp.label)}</span>
        <span class="text-gray-400"> · ${grp.idx.length}${grp.hint ? ` · ${this._esc(grp.hint)}` : ""}</span></span>
        <button type="button" data-group="${this._esc(grp.label)}" data-action="click->action-item-import#addRegisterItem" class="ml-2 text-[13px] font-medium text-brand hover:underline cursor-pointer whitespace-nowrap">+ Add here</button></span></td></tr>`)
      grp.idx.forEach(i => {
        const r = this._rows[i]
        html.push(this._rowHtml(r, i, this._groups[this._fileKey(r)] || { mode: "each" }, kindOptions, memberOptions, grp.label))
      })
    })
  }

  _saveAsToggle(value, attrs) {
    return `<span class="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white whitespace-nowrap">
      <button type="button" ${attrs} data-save="draft" class="px-2 py-0.5 text-[13px] cursor-pointer ${value !== "publish" ? "bg-brand text-white font-semibold" : "text-gray-500"}">Draft</button>
      <button type="button" ${attrs} data-save="publish" class="px-2 py-0.5 text-[13px] border-l border-gray-300 cursor-pointer ${value === "publish" ? "bg-brand text-white font-semibold" : "text-gray-500"}">Publish</button></span>`
  }

  _rowHtml(r, i, g, kindOptions, memberOptions, heading = null) {
    const inOne = g.mode === "one"
    const muted = !r.include ? "opacity-50" : ""
    const lineNo = inOne ? `<span class="text-gray-400">line</span>` : `<select data-i="${i}" data-field="kind_id" data-action="change->action-item-import#rowField" class="px-2 py-1 text-[13px] border ${r.flags.includes("question") ? "border-amber-300" : "border-gray-200"} rounded-lg bg-white cursor-pointer">${kindOptions(r.kind_id)}</select>`
    const specs = inOne ? `<span class="text-gray-400">—</span>` : `<div class="flex items-center gap-1 flex-wrap min-w-[170px]">
        ${(r.spec_drawings || []).map(id => `<span class="inline-flex items-center gap-1 rounded px-1.5 py-px text-[12px] font-medium bg-white text-gray-600 border border-gray-300">${this._esc(id)}<button type="button" data-i="${i}" data-id="${this._esc(id)}" data-action="click->action-item-import#removeSpec" class="text-gray-400 hover:text-gray-600 cursor-pointer" title="Remove">✕</button></span>`).join("")}
        <input type="text" list="aii-catalog" placeholder="+ add" data-i="${i}" data-action="change->action-item-import#addSpec keydown->action-item-import#specKeydown" class="w-16 px-1 py-0.5 text-[12px] border border-dashed border-gray-300 rounded bg-white">
        ${r.spec_drawings_by_ai && r.spec_drawings.length ? `<span class="text-[11px] text-gray-400">Picked by AI</span>` : ""}
      </div>`
    const due = inOne ? `<span class="text-gray-400">with the item</span>` : `<input type="text" data-controller="datepicker" value="${this._esc(r.due_date || "")}" data-i="${i}" data-field="due_date" data-action="change->action-item-import#rowField" placeholder="Due" class="w-28 px-2 py-1 text-[13px] border ${r.flags.includes("due_after_submit_by") || r.flags.includes("submit_by_past") ? "border-amber-300 bg-amber-50" : "border-gray-200"} rounded-lg bg-white">`
    const saveAs = inOne ? `<span class="text-gray-400">—</span>` : this._saveAsToggle(r.save_as, `data-i="${i}" data-action="click->action-item-import#rowSaveAs"`)
    const pkg = inOne || r.kind !== "submittal" ? `<span class="text-gray-400">${inOne ? "—" : "none"}</span>` : `<input type="text" list="aii-packages" value="${this._esc(r.package || "")}" placeholder="none" data-i="${i}" data-field="package" data-action="change->action-item-import#rowField" class="w-36 px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white">`
    const desc = inOne ? `<input type="text" value="${this._esc(r.description || "")}" placeholder="Note on this line" data-i="${i}" data-field="description" data-action="input->action-item-import#rowField" class="w-full min-w-[180px] px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white">`
      : `<textarea rows="1" data-i="${i}" data-field="description" data-action="input->action-item-import#rowField" class="w-full min-w-[200px] px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white resize-y">${this._esc(r.description || "")}</textarea>`
    const flags = []
    if (r.flags.includes("question") && this.formalValue) flags.push(`✦ AI read this as a question, so it's an RFI${r.include ? "" : " and unticked"}. Change the type if that's wrong.`)
    if (r.flags.includes("due_after_submit_by")) flags.push(`⚠ The list said ${this._fmtDate(r.due_date)}, but working back from the Schedule it should be in by ${this._fmtDate(r.submit_by)} (needed on site ${this._fmtDate(r.needed_on_site)} − ${r.lead_time_days || 0} days lead − ${r.review_days || 0} days review).`)
    if (r.flags.includes("submit_by_past")) flags.push(`⚠ Worked back from the Schedule, this should already have been submitted (${this._fmtDate(r.submit_by)}).`)
    // Register rows can be dragged by their handle and dropped on (heading).
    const dropOn = heading === null ? "" : `data-group="${this._esc(heading)}" data-drop-i="${i}" data-action="dragover->action-item-import#rowDragOver dragleave->action-item-import#rowDragLeave drop->action-item-import#rowDrop"`
    const dragRow = heading === null ? "" : `data-group="${this._esc(heading)}" data-drop-i="${i}" data-action="dragstart->action-item-import#rowDragStart dragover->action-item-import#rowDragOver dragleave->action-item-import#rowDragLeave drop->action-item-import#rowDrop dragend->action-item-import#rowDragEnd"`
    const grip = heading === null ? "" : `<span data-action="mousedown->action-item-import#rowGrip" title="Drag to move" class="inline-flex text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing mr-1 align-middle"><svg class="w-3.5 h-4" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.4"/><circle cx="7.5" cy="3" r="1.4"/><circle cx="2.5" cy="8" r="1.4"/><circle cx="7.5" cy="8" r="1.4"/><circle cx="2.5" cy="13" r="1.4"/><circle cx="7.5" cy="13" r="1.4"/></svg></span>`
    const manualBg = r.manual ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-gray-50"
    let matchRow = ""
    if (r.match && !inOne) {
      const act = r.match_action || "update"
      const btn = (value, label, rec) => `<button type="button" data-i="${i}" data-match="${value}" data-action="click->action-item-import#matchAction" class="px-2 py-0.5 rounded-md border text-[12px] cursor-pointer ${act === value ? "bg-amber-600 border-amber-600 text-white font-semibold" : "bg-white border-amber-300 text-amber-800 hover:bg-amber-100"}">${label}${rec ? " ✦" : ""}</button>`
      matchRow = `<tr class="bg-amber-50 ${muted}" ${dropOn}><td></td><td colspan="${this._cols() - 1}" class="px-3 py-1.5 text-[12px] text-amber-800">
        ⚠ Matches <b>${this._esc(r.match.kind_name || "")} ${String(r.match.number || "").padStart(3, "0")} · ${this._esc(r.match.title)}</b>${r.match.open ? " (open)" : " (closed)"}, ${this._esc(r.match.reason)}.
        <span class="inline-flex items-center gap-1.5 ml-2">${btn("update", `Update ${this._esc(r.match.kind_name || "it")} ${String(r.match.number || "").padStart(3, "0")} with this`, true)}${btn("create", "Keep both")}${btn("skip", "Skip this one")}</span>
      </td></tr>`
    }
    const flagRow = flags.length ? `<tr class="${muted}" ${dropOn}><td></td><td colspan="${this._cols() - 1}" class="px-3 pb-1.5 text-[12px] text-amber-700">${flags.map(f => this._esc(f)).join("<br>")}</td></tr>` : ""
    const tick = `<input type="checkbox" ${r.include ? "checked" : ""} data-i="${i}" data-action="change->action-item-import#rowInclude" class="rounded border-gray-300 text-brand focus:ring-brand cursor-pointer">`
    if (inOne) {
      // A checklist line keeps its text boxes: only its words and note matter.
      return `<tr class="border-b border-gray-100 align-top ${muted}" data-row="${i}">
        <td class="px-2 py-2 text-center">${tick}</td>
        <td class="px-2 py-2">${lineNo}</td>
        <td class="px-2 py-2"><input type="text" value="${this._esc(r.title || "")}" data-i="${i}" data-field="title" data-action="input->action-item-import#rowField" class="w-full min-w-[220px] px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white ${r.title ? "" : "border-rose-300"}"></td>
        <td class="px-2 py-2">${desc}</td>
        <td class="px-2 py-2">${specs}</td>
        <td class="px-2 py-2"><select data-i="${i}" data-field="assignee_email" data-action="change->action-item-import#rowField" class="px-2 py-1 text-[13px] border border-gray-200 rounded-lg bg-white cursor-pointer max-w-[170px]">${memberOptions(r.assignee_email)}</select></td>
        <td class="px-2 py-2">${due}</td>
        <td class="px-2 py-2"><span class="text-gray-400">—</span></td>
        <td class="px-2 py-2">${saveAs}</td>
        ${this.formalValue ? `<td class="px-2 py-2">${pkg}</td>` : ""}
      </tr>${matchRow}${flagRow}`
    }
    // One item each (Phelipe 2026-10-15): the row shows its values; clicking
    // any of them opens the row's pop-up with every field, and the i shows
    // the details on hover (right after the title, his ask 10-15).
    const kind = (this._lookups.kinds || []).find(k => String(k.id) === String(r.kind_id))
    const open = (inner, cls = "") => `<td class="px-2 py-2 cursor-pointer ${cls}" data-i="${i}" data-action="click->action-item-import#editRow">${inner}</td>`
    const dim = (text) => `<span class="text-gray-400">${text}</span>`
    const flagged = r.flags.includes("due_after_submit_by") || r.flags.includes("submit_by_past")
    const info = `<button type="button" data-row="${i}" data-i="${i}" aria-label="Details"
        data-action="mouseenter->action-item-import#hover mousemove->action-item-import#hoverMove mouseleave->action-item-import#hoverOut click->action-item-import#editRow"
        class="inline-flex align-text-bottom ml-1 text-gray-400 hover:text-brand cursor-pointer"><svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" /></svg></button>`
    return `<tr class="border-b border-gray-100 align-top ${manualBg} ${muted}" data-row="${i}" ${dragRow}>
      <td class="px-2 py-2 text-center whitespace-nowrap">${grip}${tick}</td>
      ${open(`<span class="inline-block px-2 py-0.5 rounded-md border whitespace-nowrap ${r.flags.includes("question") ? "border-amber-300 bg-amber-50 text-amber-800" : "border-gray-200 bg-white text-gray-700"}">${this._esc(kind ? kind.name : r.kind_name || "Choose…")}</span>`)}
      ${open(`${r.title ? `<span class="font-medium text-gray-900">${this._esc(r.title)}</span>` : `<span class="font-medium text-rose-600">Add a title</span>`}${info}${r.manual ? ` <span class="inline-block align-middle ml-1 rounded px-1.5 py-px text-[13px] font-semibold bg-amber-200 text-amber-900" title="Added by hand">Manual</span>` : ""}`, "min-w-[240px]")}
      ${open(r.description ? `<span class="text-gray-600 line-clamp-2" title="${this._esc(r.description)}">${this._esc(r.description)}</span>` : dim("—"), "min-w-[220px]")}
      ${open((r.spec_drawings || []).length ? `<span class="flex flex-wrap gap-1">${r.spec_drawings.map(id => `<span class="rounded px-1.5 py-px text-[12px] font-medium bg-white text-gray-600 border border-gray-300 whitespace-nowrap">${this._esc(id)}</span>`).join("")}</span>` : dim("—"))}
      ${open(`${r.assignee_email ? `<span class="whitespace-nowrap">${this._esc(r.assignee_name || r.assignee_email)}</span>` : (r.external_emails || []).length ? "" : dim("Choose…")}${(r.external_emails || []).length ? `<div class="text-[13px] text-blue-700 whitespace-nowrap" title="${this._esc(r.external_emails.join(", "))}">${r.assignee_email ? "+ " : ""}${r.external_emails.length} outside</div>` : ""}${r.responsible && !r.assignee_email && !(r.external_emails || []).length ? `<div class="text-[12px] text-gray-400 mt-0.5 truncate max-w-[170px]" title="${this._esc(r.responsible)}">${this._esc(r.responsible)}</div>` : ""}`)}
      ${open(r.due_date ? `<span class="whitespace-nowrap ${flagged ? "px-1.5 py-0.5 rounded bg-amber-50 text-amber-800" : ""}">${this._fmtDate(r.due_date)}</span>` : dim("—"))}
      <td class="px-2 py-2"><select data-i="${i}" data-field="priority" data-action="change->action-item-import#rowField" class="px-1.5 py-1 text-[13px] border border-gray-200 rounded-lg bg-white cursor-pointer">${["low", "medium", "high"].map(p => `<option value="${p}" ${(r.priority || "low") === p ? "selected" : ""}>${p[0].toUpperCase() + p.slice(1)}</option>`).join("")}</select></td>
      <td class="px-2 py-2">${this._saveAsToggle(r.save_as, `data-i="${i}" data-action="click->action-item-import#rowSaveAs"`)}</td>
      ${this.formalValue ? open(r.kind === "submittal" && r.package ? `<span class="whitespace-nowrap">${this._esc(r.package)}</span>` : dim("none")) : ""}
    </tr>${matchRow}${flagRow}`
  }

  // ── Assign to (Phelipe 2026-10-15) ────────────────────────────────────
  // The project team (internal: the one person it's assigned to) or outside
  // people (external: as many as needed; they get the item's link by email,
  // no login) — in the look of the project's Add members pop-up.

  openAssign(e) {
    const i = Number(e.currentTarget.dataset.i)
    if (!this._rows[i]) return
    this._assignFor = i
    this._assignTab = this._assignTab || "internal"
    this._assignQuery = ""
    this._assignError = ""
    this._assignOpen = true
    this._paintAssign()
    this.assignPickerTarget.classList.remove("hidden")
    this.assignPickerBodyTarget.querySelector("[data-assign-search]")?.focus()
  }

  closeAssign() {
    this._assignOpen = false
    if (this.hasAssignPickerTarget) this.assignPickerTarget.classList.add("hidden")
    this._renderReview()
  }

  closeAssignBackdrop(e) { if (e.target === e.currentTarget) this.closeAssign() }

  assignTab(e) {
    this._assignTab = e.currentTarget.dataset.tab === "external" ? "external" : "internal"
    this._assignQuery = ""
    this._assignError = ""
    this._paintAssign()
    this.assignPickerBodyTarget.querySelector("[data-assign-search]")?.focus()
  }

  assignSearch(e) {
    this._assignQuery = e.currentTarget.value
    const list = this.assignPickerBodyTarget.querySelector("[data-assign-list]")
    if (list) list.innerHTML = this._assignListHtml()
  }

  // One person from the team; picking them again takes them off.
  pickInternal(e) {
    const r = this._rows[this._assignFor]
    const email = e.currentTarget.dataset.email
    const m = (this._lookups.members || []).find(x => x.email === email)
    if (String(r.assignee_email || "").toLowerCase() === String(email).toLowerCase()) { r.assignee_email = ""; r.assignee_name = null }
    else { r.assignee_email = email; r.assignee_name = m ? m.name : null }
    this._paintAssign()
  }

  toggleExternal(e) {
    const r = this._rows[this._assignFor]
    const email = e.currentTarget.dataset.email
    r.external_emails = (r.external_emails || []).includes(email) ? r.external_emails.filter(x => x !== email) : (r.external_emails || []).concat(email)
    this._paintAssign()
  }

  removeAssigned(e) {
    const r = this._rows[this._assignFor]
    const email = e.currentTarget.dataset.email
    if (e.currentTarget.dataset.kind === "internal") { r.assignee_email = ""; r.assignee_name = null }
    else r.external_emails = (r.external_emails || []).filter(x => x !== email)
    this._paintAssign()
  }

  assignEmailKeydown(e) { if (e.key === "Enter") { e.preventDefault(); this.addExternal() } }

  async addExternal() {
    const r = this._rows[this._assignFor]
    const box = this.assignPickerBodyTarget.querySelector("[data-assign-email]")
    const email = String(box?.value || "").trim().toLowerCase()
    const texts = { invalidEmail: "Please enter a valid email address.", alreadyAdded: "This person is already on the list.", noSuchEmail: "This email address doesn't exist. Please check it for typos." }
    const error = await checkInviteEmail(email, { selected: new Set(r.external_emails || []), validateUrl: "/validate_email", texts })
    if (error) { this._assignError = error; this._paintAssign(); this.assignPickerBodyTarget.querySelector("[data-assign-email]")?.focus(); return }
    r.external_emails = (r.external_emails || []).concat(email)
    const known = this._lookups.external_users || (this._lookups.external_users = [])
    if (!known.includes(email)) known.push(email)
    this._assignError = ""
    this._paintAssign()
    this.assignPickerBodyTarget.querySelector("[data-assign-email]")?.focus()
  }

  _avatar(name, email, color) {
    const words = String(name || email || "?").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean)
    const letters = (words[0]?.[0] || "?") + (words[1]?.[0] || "")
    return `<span class="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[13px] font-semibold text-white" style="background:${color}">${this._esc(letters.toUpperCase())}</span>`
  }

  _assignListHtml() {
    const r = this._rows[this._assignFor]
    const fold = (t) => String(t ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    const q = fold(this._assignQuery).trim()
    if (this._assignTab === "external") {
      const people = (this._lookups.external_users || []).filter(e => !q || fold(e).includes(q))
      if (!people.length) return `<div class="px-4 py-8 text-[13px] text-gray-400 text-center">${q ? "No one matches." : "No outside people yet. Add an email below."}</div>`
      return people.map(email => `<label class="flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer hover:bg-gray-50">
          <input type="checkbox" ${(r.external_emails || []).includes(email) ? "checked" : ""} data-email="${this._esc(email)}" data-action="change->action-item-import#toggleExternal" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer">
          ${this._avatar(email, email, "#2563eb")}
          <span class="flex-1 min-w-0"><span class="block text-sm font-medium text-gray-900 truncate">${this._esc(email)}</span><span class="block text-[13px] text-gray-500">Gets the link by email</span></span>
        </label>`).join("")
    }
    const people = (this._lookups.members || []).filter(m => !q || fold(`${m.name} ${m.email}`).includes(q))
    if (!people.length) return `<div class="px-4 py-8 text-[13px] text-gray-400 text-center">${q ? "No one matches." : "No one on the project team yet."}</div>`
    return people.map(m => {
      const on = String(r.assignee_email || "").toLowerCase() === String(m.email || "").toLowerCase()
      return `<label class="flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer ${on ? "bg-brand/5" : "hover:bg-gray-50"}">
          <input type="radio" name="aii-assign" ${on ? "checked" : ""} data-email="${this._esc(m.email)}" data-action="click->action-item-import#pickInternal" class="border-gray-300 text-brand focus:ring-brand cursor-pointer">
          ${this._avatar(m.name, m.email, "#16a34a")}
          <span class="flex-1 min-w-0"><span class="block text-sm font-medium text-gray-900 truncate">${this._esc(m.name || m.email)}</span><span class="block text-[13px] text-gray-500 truncate">${this._esc(m.email)}</span></span>
        </label>`
    }).join("")
  }

  _paintAssign() {
    const r = this._rows[this._assignFor]
    if (!r || !this.hasAssignPickerBodyTarget) return
    const ext = this._assignTab === "external"
    const tab = (key, label, count) => `<button type="button" data-tab="${key}" data-action="click->action-item-import#assignTab"
        class="flex-1 px-3 py-2 text-[13px] font-semibold cursor-pointer border-b-2 ${this._assignTab === key ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}">${label} <span class="font-normal text-gray-400">${count}</span></button>`
    const chosen = []
    if (r.assignee_email) chosen.push({ kind: "internal", email: r.assignee_email, label: r.assignee_name || r.assignee_email })
    ;(r.external_emails || []).forEach(email => chosen.push({ kind: "external", email, label: email }))
    const chosenHtml = chosen.length ? chosen.map(c => {
      const tone = c.kind === "internal" ? "#16a34a" : "#2563eb"
      return `<div class="flex items-center gap-3 px-2.5 py-2 rounded-lg" style="background:${tone}0F;border:1px solid ${tone}4D">
          ${this._avatar(c.label, c.email, tone)}
          <span class="flex-1 min-w-0"><span class="block text-sm font-medium text-gray-900 truncate">${this._esc(c.label)}</span>
            <span class="block text-[13px] text-gray-500 truncate">${c.kind === "internal" ? (c.label !== c.email ? this._esc(c.email) : "Project team") : "Outside · gets the link by email, no login"}</span></span>
          <span class="shrink-0 rounded-full px-2 py-px text-[13px] font-semibold" style="background:${tone}24;color:${tone}">${c.kind === "internal" ? "Assigned to" : "Outside"}</span>
          <button type="button" data-kind="${c.kind}" data-email="${this._esc(c.email)}" data-action="click->action-item-import#removeAssigned" class="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer" title="Remove">✕</button>
        </div>`
    }).join("") : `<div class="px-3 py-8 text-[13px] text-gray-400 text-center">No one yet. Pick someone from the team, or add outside people.</div>`
    this.assignPickerBodyTarget.innerHTML = `
      <div class="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-gray-900">Assign to</h3>
          <p class="text-[13px] text-gray-500 truncate">${this._esc(r.title || "This item")}</p>
        </div>
        <button type="button" data-action="click->action-item-import#closeAssign" class="shrink-0 -mr-1 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer" aria-label="Close">✕</button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 min-h-0 md:h-[440px]">
        <div class="flex flex-col min-h-0 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div class="flex border-b border-gray-200">${tab("internal", "Project team", (this._lookups.members || []).length)}${tab("external", "Outside people", (this._lookups.external_users || []).length)}</div>
          <div class="p-2.5 border-b border-gray-100">
            <input type="search" data-assign-search value="${this._esc(this._assignQuery || "")}" data-action="input->action-item-import#assignSearch"
                   placeholder="${ext ? "Search outside people…" : "Search the team…"}" autocomplete="off" data-1p-ignore data-lpignore="true"
                   class="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
          </div>
          <div data-assign-list class="flex-1 min-h-[10rem] max-h-[40vh] md:max-h-none overflow-y-auto p-1.5 space-y-0.5">${this._assignListHtml()}</div>
          ${ext ? `<div class="border-t border-gray-100 px-3 py-3">
            <div class="flex items-center gap-2">
              <input type="email" data-assign-email data-action="keydown->action-item-import#assignEmailKeydown" placeholder="name@company.com"
                     autocomplete="off" data-1p-ignore data-lpignore="true" data-form-type="other"
                     class="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
              <button type="button" data-action="click->action-item-import#addExternal" class="shrink-0 px-3.5 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark cursor-pointer">Add</button>
            </div>
            ${this._assignError ? `<p class="mt-1.5 text-[13px] text-red-600">${this._esc(this._assignError)}</p>` : ""}
            <p class="mt-1.5 text-[13px] text-gray-500">Outside people get the item's link by email when it's published. No login needed.</p>
          </div>` : `<p class="border-t border-gray-100 px-4 py-2.5 text-[13px] text-gray-500">One person from the team is assigned. Outside people can be added too.</p>`}
        </div>
        <div class="flex flex-col min-h-0 rounded-xl border border-gray-200 bg-gray-50/60 overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200 text-[13px] font-semibold text-gray-700">On this item <span class="font-normal text-gray-400">${chosen.length || ""}</span></div>
          <div class="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">${chosenHtml}</div>
        </div>
      </div>
      <div class="flex justify-end gap-2 px-6 py-4">
        <button type="button" data-action="click->action-item-import#closeAssign" class="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-brand hover:bg-brand-dark cursor-pointer">Done</button>
      </div>`
  }

  // ── The row's pop-up: every field of one item ─────────────────────────

  editRow(e) {
    const i = Number(e.currentTarget.dataset.i)
    if (!this._rows[i]) return
    this._hideHover()
    this._editing = i
    this._editorOpen = true
    this._paintEditor()
    this.rowEditorTarget.classList.remove("hidden")
    this.rowEditorBodyTarget.querySelector("[data-field='title']")?.focus()
  }

  closeRowEditor() {
    this._settleManual(this._editing)
    this._closeEditorQuietly()
    this._renderReview()
    if (this._scrollToRow != null) {
      this.reviewBodyTarget.querySelector(`tr[data-row="${this._scrollToRow}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" })
      this._scrollToRow = null
    }
  }

  closeRowEditorBackdrop(e) { if (e.target === e.currentTarget) this.closeRowEditor() }

  _closeEditorQuietly() {
    this._editorOpen = false
    this._editing = null
    if (this.hasRowEditorTarget) this.rowEditorTarget.classList.add("hidden")
  }

  // ‹ Previous / Next › walk the items without closing the pop-up.
  editorStep(e) {
    const order = this._visibleOrder().filter(i => (this._groups[this._fileKey(this._rows[i])] || {}).mode !== "one")
    const at = order.indexOf(this._editing)
    const next = order[at + Number(e.currentTarget.dataset.step)]
    if (next === undefined) return
    this._settleManual(this._editing)
    this._editing = next
    this._paintEditor()
    this._renderReview()
  }

  _paintEditor() {
    const i = this._editing
    const r = this._rows[i]
    if (r == null || !this.hasRowEditorBodyTarget) return
    const kinds = this._lookups.kinds || []
    const members = this._lookups.members || []
    const order = this._visibleOrder().filter(k => (this._groups[this._fileKey(this._rows[k])] || {}).mode !== "one")
    const at = order.indexOf(i)
    const input = "w-full px-2.5 py-1.5 text-[13px] border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    const field = (label, inner, wide = false) => `<label class="block ${wide ? "md:col-span-2" : ""}"><span class="block text-[13px] font-medium text-gray-600 mb-1">${label}</span>${inner}</label>`
    const num = (name, value) => `<input type="number" min="0" value="${this._esc(value ?? "")}" data-i="${i}" data-field="${name}" data-action="change->action-item-import#rowDates" class="${input}">`
    const flags = []
    if (r.flags.includes("question") && this.formalValue) flags.push("AI read this as a question, so it's an RFI. Change the type if that's wrong.")
    if (r.flags.includes("due_after_submit_by")) flags.push(`The list said ${this._fmtDate(r.due_date)}, but working back from the Schedule it should be in by ${this._fmtDate(r.submit_by)}.`)
    if (r.flags.includes("submit_by_past")) flags.push(`Worked back from the Schedule, this should already have been submitted (${this._fmtDate(r.submit_by)}).`)
    const specs = `<div class="flex items-center gap-1.5 flex-wrap rounded-lg border border-gray-300 bg-white px-2 py-1.5">
        ${(r.spec_drawings || []).map(id => `<span class="inline-flex items-center gap-1 rounded px-1.5 py-px text-[13px] font-medium bg-gray-50 text-gray-700 border border-gray-300">${this._esc(id)}<button type="button" data-i="${i}" data-id="${this._esc(id)}" data-action="click->action-item-import#removeSpec" class="text-gray-400 hover:text-gray-600 cursor-pointer" title="Remove">✕</button></span>`).join("")}
        <input type="text" list="aii-catalog" placeholder="+ add a drawing or spec" data-i="${i}" data-action="change->action-item-import#addSpec keydown->action-item-import#specKeydown" class="flex-1 min-w-[140px] px-1 py-0.5 text-[13px] border-0 focus:outline-none focus:ring-0">
      </div>${r.spec_drawings_by_ai && (r.spec_drawings || []).length ? `<span class="block mt-0.5 text-[13px] text-gray-400">Picked by AI</span>` : ""}`
    this.rowEditorBodyTarget.innerHTML = `
      <div class="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-200">
        <div class="min-w-0">
          <h4 class="text-base font-semibold text-gray-900">${r.manual && !r.settled ? "New item" : "Edit item"}</h4>
          <p class="text-[13px] text-gray-500">${r.manual && !r.settled ? "Added by hand — give it a title; it goes under the heading its spec section belongs to." : `Item ${at + 1} of ${order.length}${r.found_in ? ` · found in ${this._esc(r.found_in)}` : ""}`}</p>
        </div>
        <label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer whitespace-nowrap">
          <input type="checkbox" ${r.include ? "checked" : ""} data-i="${i}" data-action="change->action-item-import#rowInclude" class="rounded border-gray-300 text-brand focus:ring-brand cursor-pointer"> Include
        </label>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
        ${flags.length ? `<div class="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">${flags.map(f => this._esc(f)).join("<br>")}</div>` : ""}
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          ${field("Type", `<select data-i="${i}" data-field="kind_id" data-action="change->action-item-import#rowField" class="${input} cursor-pointer">${kinds.map(k => `<option value="${k.id}" ${String(k.id) === String(r.kind_id || "") ? "selected" : ""}>${this._esc(k.name)}</option>`).join("")}</select>`)}
          ${field("Assigned to", `<button type="button" data-i="${i}" data-action="click->action-item-import#openAssign" class="${input} text-left cursor-pointer flex items-center gap-2">
            <span class="flex-1 min-w-0 truncate">${r.assignee_email ? this._esc(r.assignee_name || r.assignee_email) : (r.external_emails || []).length ? "" : `<span class="text-gray-400">Choose…</span>`}${(r.external_emails || []).length ? `<span class="text-blue-700">${r.assignee_email ? " + " : ""}${r.external_emails.length} outside</span>` : ""}</span>
            <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>`)}
          ${field("Title", `<input type="text" value="${this._esc(r.title || "")}" data-i="${i}" data-field="title" data-action="input->action-item-import#rowField" class="${input} ${r.title ? "" : "border-rose-300"}">`, true)}
          ${field("Quick description", `<textarea rows="3" data-i="${i}" data-field="description" data-action="input->action-item-import#rowField" class="${input} resize-y">${this._esc(r.description || "")}</textarea>`, true)}
          ${this._isRegister() ? field("Spec section", `<input type="text" list="aii-sections" value="${this._esc(r.spec_section || "")}" placeholder="e.g. 03 30 00" data-i="${i}" data-action="change->action-item-import#rowSection" class="${input}"><span class="block mt-0.5 text-[13px] text-gray-400">Sets the heading it goes under.</span>`) : ""}
          ${field("Drawings & Specs", specs, true)}
          ${field(this.formalValue ? "Submit by" : "Due", `<input type="text" data-controller="datepicker" value="${this._esc(r.due_date || "")}" data-i="${i}" data-field="due_date" data-action="change->action-item-import#rowField" class="${input}">`)}
          ${field("Save as", this._saveAsToggle(r.save_as, `data-i="${i}" data-action="click->action-item-import#rowSaveAs"`))}
          ${this.formalValue && r.kind === "submittal" ? field("Package", `<input type="text" list="aii-packages" value="${this._esc(r.package || "")}" placeholder="none" data-i="${i}" data-field="package" data-action="change->action-item-import#rowField" class="${input}">`) : ""}
          ${field("Priority", `<select data-i="${i}" data-field="priority" data-action="change->action-item-import#rowField" class="${input} cursor-pointer">${["high", "medium", "low"].map(p => `<option value="${p}" ${(r.priority || "low") === p ? "selected" : ""}>${p[0].toUpperCase() + p.slice(1)}</option>`).join("")}</select>`)}
          ${this.formalValue ? `
            ${field("Needed on site", `<input type="text" data-controller="datepicker" value="${this._esc(r.needed_on_site || "")}" data-i="${i}" data-field="needed_on_site" data-action="change->action-item-import#rowDates" class="${input}">${r.task_name ? `<span class="block mt-0.5 text-[13px] text-gray-400 truncate">${this._esc(r.task_name)} · Schedule</span>` : ""}`)}
            ${field("Lead time (days)", num("lead_time_days", r.lead_time_days))}
            ${field("Review (days)", num("review_days", r.review_days))}
            ${field("Worked back from the Schedule", `<span class="block px-1 py-1.5 font-semibold text-gray-900">${r.submit_by ? this._fmtDate(r.submit_by) : "—"}</span>`)}` : ""}
          ${field("Responsible (as written)", `<input type="text" value="${this._esc(r.responsible || "")}" data-i="${i}" data-field="responsible" data-action="input->action-item-import#rowField" class="${input}">`)}
          ${field("Source", r.manual ? `<span class="block px-1 py-1.5 text-gray-700">Added by hand</span>` : `<span class="block px-1 py-1.5 text-gray-700">${this._esc(this._fileName(this._fileKey(r)))}${r.pages ? ` · pages ${r.pages[0]}–${r.pages[1]}` : r.source_row ? ` · row ${r.source_row}` : ""}</span>`)}
        </div>
      </div>
      <div class="flex items-center gap-2 px-5 py-3 border-t border-gray-200">
        <button type="button" data-step="-1" data-action="click->action-item-import#editorStep" ${at <= 0 ? "disabled" : ""} class="px-3 py-1.5 rounded-lg text-[13px] text-gray-700 border border-gray-300 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-default">‹ Previous</button>
        <button type="button" data-step="1" data-action="click->action-item-import#editorStep" ${at >= order.length - 1 ? "disabled" : ""} class="px-3 py-1.5 rounded-lg text-[13px] text-gray-700 border border-gray-300 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-default">Next ›</button>
        <button type="button" data-action="click->action-item-import#closeRowEditor" class="ml-auto px-4 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-brand hover:bg-brand-dark cursor-pointer">Done</button>
      </div>`
  }

  // ── Review edits ──────────────────────────────────────────────────────

  rowField(e) {
    const el = e.currentTarget
    const i = Number(el.dataset.i)
    const r = this._rows[i]
    if (!r) return
    const f = el.dataset.field
    const v = el.value
    r[f] = v
    if (f === "kind_id") {
      const k = (this._lookups.kinds || []).find(k => String(k.id) === String(v))
      r.kind_name = k ? k.name : r.kind_name
      r.kind = k && /submittal/i.test(k.name) ? "submittal" : k && /rfi/i.test(k.name) ? "rfi" : k && /punch|checklist/i.test(k.name) ? "punchlist" : "other"
      r.kind_set_by_user = true
      this._renderReview()
    }
    if (f === "assignee_email") { const m = (this._lookups.members || []).find(m => m.email === v); r.assignee_name = m ? m.name : null }
    if (f === "title" || f === "kind_id" || f === "package") this._paintSummary()
  }

  rowDates(e) {
    const el = e.currentTarget
    const i = Number(el.dataset.i)
    const r = this._rows[i]
    if (!r) return
    r[el.dataset.field] = el.value
    if (r.needed_on_site) {
      const d = new Date(r.needed_on_site + "T00:00:00")
      d.setDate(d.getDate() - Number(r.lead_time_days || 0) - Number(r.review_days || 0))
      r.submit_by = d.toISOString().slice(0, 10)
      r.due_date = r.submit_by
      r.flags = r.flags.filter(f => f !== "due_after_submit_by" && f !== "submit_by_past")
      if (new Date(r.submit_by) < new Date(new Date().toDateString())) r.flags.push("submit_by_past")
    }
    this._renderReview()
  }

  rowInclude(e) {
    const i = Number(e.currentTarget.dataset.i)
    if (this._rows[i]) { this._rows[i].include = e.currentTarget.checked; this._renderReview() }
  }

  rowSaveAs(e) {
    const i = Number(e.currentTarget.dataset.i)
    if (this._rows[i]) { this._rows[i].save_as = e.currentTarget.dataset.save; this._renderReview() }
  }

  matchAction(e) {
    const i = Number(e.currentTarget.dataset.i)
    if (this._rows[i]) { this._rows[i].match_action = e.currentTarget.dataset.match; this._renderReview() }
  }

  addSpec(e) {
    const i = Number(e.currentTarget.dataset.i)
    const id = e.currentTarget.value.trim()
    e.currentTarget.value = ""
    if (!id || !this._rows[i]) return
    const known = (this._lookups.catalog || []).some(c => c.id === id)
    if (!known) { this._toast(`"${id}" isn't a drawing or spec on this project.`, "alert"); return }
    if (!this._rows[i].spec_drawings.includes(id)) this._rows[i].spec_drawings.push(id)
    this._renderReview()
  }

  specKeydown(e) { if (e.key === "Enter") { e.preventDefault(); this.addSpec(e) } }

  removeSpec(e) {
    const i = Number(e.currentTarget.dataset.i)
    const id = e.currentTarget.dataset.id
    if (this._rows[i]) { this._rows[i].spec_drawings = this._rows[i].spec_drawings.filter(x => x !== id); this._renderReview() }
  }

  pickGroupMode(e) {
    const key = e.currentTarget.dataset.key
    const g = this._groups[key]
    if (!g) return
    g.mode = e.currentTarget.dataset.mode === "one" ? "one" : "each"
    if (g.mode === "one") this._applyOneDefaults(key)
    this._renderReview()
    this._paintFooter()
  }

  groupField(e) {
    const g = this._groups[e.currentTarget.dataset.key]
    if (g) { g[e.currentTarget.dataset.field] = e.currentTarget.value; this._paintSummary() }
  }

  groupSaveAs(e) {
    const g = this._groups[e.currentTarget.dataset.key]
    if (g) { g.save_as = e.currentTarget.dataset.save; this._renderReview() }
  }

  typeForAll(e) {
    const key = e.currentTarget.dataset.key
    const v = e.currentTarget.value
    if (!v) return
    const k = (this._lookups.kinds || []).find(k => String(k.id) === String(v))
    this._rows.filter(r => key === "all" || this._fileKey(r) === key).forEach(r => {
      if (r.kind_set_by_user) return
      // A row the AI typed differently keeps its type (his rule).
      if (r.kind && r.default_kind_id && String(r.kind_id) !== String(r.default_kind_id) && !r.flags.includes("question")) return
      r.kind_id = k.id; r.kind_name = k.name
      r.kind = /submittal/i.test(k.name) ? "submittal" : /rfi/i.test(k.name) ? "rfi" : /punch|checklist/i.test(k.name) ? "punchlist" : "other"
    })
    this._renderReview()
  }

  // ── Hover card ────────────────────────────────────────────────────────

  hover(e) {
    const i = Number(e.currentTarget.dataset.row)
    const r = this._rows[i]
    if (!r) return
    const row = (k, v) => v ? `<div class="grid grid-cols-[110px_1fr] gap-2 py-0.5"><span class="text-gray-400">${k}</span><span>${v}</span></div>` : ""
    const kind = (this._lookups.kinds || []).find(k => String(k.id) === String(r.kind_id))
    this.hoverCardTarget.innerHTML = `<div class="font-semibold text-gray-900 mb-1">${this._esc(r.title || "")}</div>
      ${row("Type", this._esc(kind ? kind.name : r.kind_name || ""))}
      ${row("Description", this._esc(r.description || ""))}
      ${row("Drawings & Specs", this._esc((r.spec_drawings || []).join(", ")))}
      ${row("Assigned To", this._esc(r.assignee_name || r.assignee_email || ((r.external_emails || []).length ? "" : r.responsible) || ""))}
      ${row("Outside people", this._esc((r.external_emails || []).join(", ")))}
      ${row("Due", this._fmtDate(r.due_date))}
      ${row("Needed on site", r.needed_on_site ? `${this._fmtDate(r.needed_on_site)}${r.task_name ? ` · ${this._esc(r.task_name)}` : ""}` : "")}
      ${row("Lead time", r.lead_time_days ? `${r.lead_time_days} days` : "")}
      ${row("Review", r.review_days ? `${r.review_days} days` : "")}
      ${row("Submit by", this._fmtDate(r.submit_by))}
      ${row("Priority", r.priority ? r.priority[0].toUpperCase() + r.priority.slice(1) : "Low")}
      ${row("Package", this._esc(r.package || ""))}
      ${row("Found in", this._esc(r.found_in || ""))}
      ${row("Source", `${this._esc(this._fileName(this._fileKey(r)))}${r.pages ? ` · pages ${r.pages[0]}–${r.pages[1]}` : r.source_row ? ` · row ${r.source_row}` : ""}`)}
      <div class="mt-1 text-[13px] text-gray-400">Click the row to edit everything.</div>`
    this.hoverCardTarget.classList.remove("hidden")
    this.hoverMove(e)
  }

  hoverMove(e) {
    const card = this.hoverCardTarget
    if (card.classList.contains("hidden")) return
    const w = card.offsetWidth, h = card.offsetHeight
    let x = e.clientX + 16, y = e.clientY + 16
    if (x + w > window.innerWidth - 8) x = e.clientX - w - 16
    if (y + h > window.innerHeight - 8) y = window.innerHeight - h - 8
    card.style.left = `${Math.max(8, x)}px`
    card.style.top = `${Math.max(8, y)}px`
  }

  hoverOut() { this._hideHover() }
  _hideHover() { if (this.hasHoverCardTarget) this.hoverCardTarget.classList.add("hidden") }

  // ── Summary + save ────────────────────────────────────────────────────

  _summarize() {
    let toSave = 0, updates = 0, drafts = 0, checklists = 0, unticked = 0, toCheck = 0, manual = 0
    const kinds = {}
    const packages = new Set()
    this._fileKeys().forEach(key => {
      const g = this._groups[key]
      const rows = this._rows.filter(r => this._fileKey(r) === key && this._inSize(r))
      const inc = rows.filter(r => r.include)
      unticked += rows.length - inc.length
      if (!inc.length) return
      if (g.mode === "one") { toSave += 1; checklists += 1; if (g.save_as === "draft") drafts += 1; return }
      inc.forEach(r => {
        if (r.match && (r.match_action || "update") === "skip") { unticked += 1; return }
        if (r.match && (r.match_action || "update") === "update") updates += 1
        toSave += 1
        if (r.save_as === "draft") drafts += 1
        if (r.package) packages.add(r.package)
        if (r.flags.length) toCheck += 1
        if (r.manual) manual += 1
        const name = r.kind_name || "item"
        kinds[name] = (kinds[name] || 0) + 1
      })
    })
    const parts = Object.entries(kinds).map(([k, n]) => `${n} ${k.toLowerCase()}${n === 1 ? "" : "s"}`)
    if (checklists) parts.push(`${checklists} with a checklist`)
    let text = toSave ? `${toSave} selected` + (parts.length ? ` · ${parts.join(" + ")}` : "") : "Nothing selected"
    if (updates) text += ` · ${updates} update${updates === 1 ? "" : "s"}`
    if (packages.size) text += ` · ${packages.size} package${packages.size === 1 ? "" : "s"}`
    if (drafts) text += ` · ${drafts} as draft${drafts === 1 ? "" : "s"}`
    if (unticked) text += ` · ${unticked} left out`
    if (toCheck) text += ` · ${toCheck} to check`
    if (manual) text += ` · ${manual} added by hand`
    return { toSave, updates, drafts, checklists, unticked, toCheck, text }
  }

  _paintSummary() {
    if (this.hasReviewSearchCountTarget) {
      const inSize = this._rows ? this._rows.filter(r => this._inSize(r)).length : 0
      const shown = this._rows ? this._rows.filter(r => this._rowShown(r)).length : 0
      this.reviewSearchCountTarget.textContent = this._query && this._query.trim() ? `${shown} of ${inSize}` : ""
    }
    this._paintSize()
    const s = this._summarize()
    this.reviewSummaryTarget.innerHTML = `<span class="font-semibold text-gray-900">${this._esc(s.text)}</span>`
    const notes = []
    if (this._isRegister() && this._size !== "full") {
      const left = this._rows.length - this._sizeCount(this._size)
      const name = this._size === "essentials" ? "Essentials" : "Important"
      notes.push(`${name} list: the other ${left} of the ${this._rows.length} items read are hidden and won't be saved. Pick Full list to see them again.`)
    }
    if (s.drafts) notes.push(`${s.drafts} will be saved as draft${s.drafts === 1 ? "" : "s"} — drafts stay hidden from the reviewers until you publish them.`)
    if (s.updates) notes.push(`${s.updates} existing item${s.updates === 1 ? "" : "s"} will be updated with what the file says; nothing in their chat is lost.`)
    this.reviewNotesTarget.innerHTML = notes.map(n => `<p class="text-[12px] text-gray-500">${this._esc(n)}</p>`).join("")
    this._paintFooter()
  }

  async _apply() {
    const btn = this.primaryBtnTarget
    btn.disabled = true
    const old = btn.textContent
    btn.textContent = "Saving…"
    const rows = this._displayOrder().map(i => this._rows[i]).map(r => ({
      i: r.i, file: r.file, file_name: r.file_name, include: r.include && this._inSize(r), kind: r.kind, kind_id: r.kind_id, kind_name: r.kind_name,
      title: r.title, description: r.description, spec_section: r.spec_section, spec_drawings: r.spec_drawings, found_in: r.found_in,
      submittal_type: r.submittal_type, responsible: r.responsible, assignee_email: r.assignee_email, due_date: r.due_date,
      needed_on_site: r.needed_on_site, lead_time_days: r.lead_time_days, review_days: r.review_days, task_id: r.task_id,
      package: r.package, pages: r.pages, priority: r.priority, save_as: r.save_as, match: r.match, match_action: r.match ? (r.match_action || "update") : null,
      external_emails: r.external_emails || []
    }))
    try {
      const res = await fetch(this.applyUrlValue.replace("IMPORT_ID", this._import), {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify({ rows, groups: this._groups })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Couldn't save the items.")
      const n = (data.created || []).length, u = (data.updated || []).length, c = (data.checklists || []).length
      const bits = []
      if (n) bits.push(`${n} action item${n === 1 ? "" : "s"} created${c ? ` (${c} with a checklist)` : ""}`)
      if (u) bits.push(`${u} updated`)
      if ((data.packages || []).length) bits.push(`${data.packages.length} package${data.packages.length === 1 ? "" : "s"}`)
      const msg = bits.join(" · ") || "Nothing was created."
      this._showPane("done")
      this.doneTextTarget.textContent = msg
      sessionStorage.setItem("actionItemToast", JSON.stringify({ message: msg, type: "notice" }))
      this._paintFooter()
    } catch (err) {
      btn.disabled = false
      btn.textContent = old
      this._toast(err.message || "Couldn't save the items.", "alert")
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  _csrf() { return document.querySelector('meta[name="csrf-token"]')?.content }

  _esc(text) {
    return String(text ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _fmtDate(iso) {
    if (!iso) return ""
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""))
    return isNaN(d) ? this._esc(iso) : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = `flash-toast-${type === "alert" ? "alert" : "notice"}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "6000")
    el.setAttribute("role", "alert")
    el.innerHTML = `<p class="flex-1 text-sm">${this._esc(message)}</p>`
    document.body.appendChild(el)
  }
}
